//utils.firestore.js
import { db, auth, app } from '../firebase'; 
import { collection, getDocs, doc, getDoc, setDoc, onSnapshot, addDoc, query, where, updateDoc, FieldValue, writeBatch, deleteDoc, deleteField, serverTimestamp, limit, orderBy, startAfter, getCountFromServer, Timestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import pako from 'pako';
import { FIELD_TYPE_CALCULATED } from '../components/Fields/constants/fieldTypes';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { resolveLayoutConfig, getFormVersionSnapshot, mapViewModeToResolverMode } from './formVersionResolver';

const { v4: uuidv4 } = require('uuid');


// Fetch the dropdown field labels (single-select and multi-select fields) for a given formId
export const getDropdownLabels = async (formId) => {
  try {
    const formFieldsCollection = collection(db, 'forms', formId, 'fields');
    const formFieldsQuery = query(formFieldsCollection, where('type', 'in', ['single-select', 'multi-select']));
    const formFieldsSnapshot = await getDocs(formFieldsQuery);

    const fieldLabels = formFieldsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        label: data.label,
        optionsSetUFI: data.optionsSetUFI,
        multiselect: data.multiselect || false,
        fieldType: data.type === 'multi-select' ? 'multi-select' : 'single-select',
        options: Array.isArray(data.options) ? data.options.map(option => ({
          label: option.label || 'Default Label',
          value: option.value || '',
          hint: option.hint || '',
          order: option.order || 0
        })) : [] // Ensure options is always an array
      };
    });

    return fieldLabels;
  } catch (error) {
    console.error("Error fetching dropdown labels:", error);
    throw error;
  }
};




// Function to directly fetch dropdown options for given optionsSetUFIs
export const getDropdownOptions = async (optionsSetUFIs, setLoading, setError) => {
//  console.log(`[getDropdownOptions] Called with optionsSetUFIs:`, optionsSetUFIs);

  if (!Array.isArray(optionsSetUFIs)) {
    console.error(`[getDropdownOptions] Invalid input: optionsSetUFIs is not an array.`);
    setError(new Error("[getDropdownOptions] Invalid input: optionsSetUFIs is not an array."));
    return {}; // Return an empty object
  }

  const options = {};
  
  try {
    for (const ufi of optionsSetUFIs) {
      const dropdownOptionRef = doc(db, "dropdownOptions", ufi);
      const dropdownOptionSnap = await getDoc(dropdownOptionRef);

      if (dropdownOptionSnap.exists()) {
        const fetchedOptions = dropdownOptionSnap.data().options || [];  // Ensure options is always an array
        options[ufi] = fetchedOptions.map(option => ({
          ...option,
          optionId: option.optionId || uuidv4(),
          label: option.label || 'Default Label',
          value: option.value || '',
          hint: option.hint || '',
          order: option.order || 1
        }));
      } else {
        options[ufi] = [];  // Return an empty array if no options are found
//        console.log(`No options found for optionsSetUFI: ${ufi}`);
      }
    }
  } catch (error) {
    console.error(`[getDropdownOptions] Error fetching options:`, error);
    throw error;
  }

  return options;
};


// Function to fetch dropdown options in batches for given optionsSetUFIs
export const getDropdownOptionsBatch = async (optionsSetUFIs, startAt = 0, limit = 50) => {
//  console.log(`[getDropdownOptionsBatch] Fetching options for UFIs:`, optionsSetUFIs);

  if (!Array.isArray(optionsSetUFIs)) {
    console.error(`[getDropdownOptionsBatch] Invalid input: optionsSetUFIs is not an array.`);
    return {};
  }

  const options = {};
  try {
    for (const ufi of optionsSetUFIs) {
      const dropdownOptionRef = doc(db, "dropdownOptions", ufi);
      const dropdownOptionSnap = await getDoc(dropdownOptionRef);

      if (dropdownOptionSnap.exists()) {
        const allOptions = dropdownOptionSnap.data().options || []; // Ensure options is always an array
        options[ufi] = allOptions.slice(startAt, startAt + limit).map(option => ({
          ...option,
          optionId: option.optionId || uuidv4(),
          label: option.label || 'Default Label',
          value: option.value || '',
          hint: option.hint || '',
          order: option.order || 1
        }));
      } else {
        options[ufi] = []; // Default to empty array if no options exist
      }
    }
  } catch (error) {
    console.error(`[getDropdownOptionsBatch] Error fetching options:`, error);
    throw error;
  }

  return options;
};


// Function to subscribe to dropdown options changes for specific forms
export const subscribeToDropdownOptions = async (formId, callback) => {
//  console.log(`[subscribeToDropdownOptions] Starting subscription for form ID: ${formId}`);

  const fieldMetadataRef = collection(db, 'fieldMetadata');
  const metadataQuery = query(fieldMetadataRef, where('formId', '==', formId));

  let metadataSnapshot;
  try {
    metadataSnapshot = await getDocs(metadataQuery);
  } catch (error) {
    console.error(`[subscribeToDropdownOptions] Error fetching metadata for form ID ${formId}:`, error);
    return () => {}; // Return a no-op unsubscribe function in case of initial fetch failure
  }

  const optionsSetUFIs = [];
  metadataSnapshot.forEach((doc) => {
    const data = doc.data();
    if (['single-select', 'multi-select'].includes(data.type) && data.optionsSetUFI) {
      optionsSetUFIs.push(data.optionsSetUFI);
    }
  });

  if (optionsSetUFIs.length === 0) {
//    console.log(`[subscribeToDropdownOptions] No dropdown optionsSetUFIs found for form ID: ${formId}.`);
    return () => {}; // Return a no-op function if no options set UFIs are found
  }

  const unsubscribeHandles = optionsSetUFIs.map((optionsSetUFI) => {
    const optionsRef = doc(db, 'dropdownOptions', optionsSetUFI);
    return onSnapshot(optionsRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const updatedOptions = docSnapshot.data().options || []; // Ensure options is always an array
        const formattedOptions = updatedOptions.map(option => ({
          optionId: option.optionId || uuidv4(),
          label: option.label,
          value: option.value || null, 
          hint: option.hint || '', 
          order: option.order || 1 
        }));
//        console.log(`[subscribeToDropdownOptions] Received updated options for optionsSetUFI: ${optionsSetUFI}`);
        callback({ [optionsSetUFI]: formattedOptions });
      } else {
        callback({ [optionsSetUFI]: [] }); // Return empty array if no data is found
//        console.log(`[subscribeToDropdownOptions] No data found for optionsSetUFI: ${optionsSetUFI}`);
      }
    }, (error) => {
      console.error(`[subscribeToDropdownOptions] Error in subscription for optionsSetUFI ${optionsSetUFI}:`, error);
    });
  });

  return () => unsubscribeHandles.forEach(unsubscribe => unsubscribe());
};





// Function to update dropdown options for a specific optionsSetUFI
export const updateDropdownOptions = async (optionsSetUFI, newOptions) => {
//  console.log(`[updateDropdownOptions] Updating dropdown options for optionsSetUFI '${optionsSetUFI}' with data:`, newOptions);

  // Validate the input before proceeding
  if (!Array.isArray(newOptions) || newOptions.some(option => option.order == null)) {
    console.error("[updateDropdownOptions] Invalid input: newOptions must be an array and each option should have an 'order'.");
    throw new Error("Invalid input: each option must have an 'order'.");
  }

  // Map through newOptions and ensure the structure aligns with your dropdown metadata
  const formattedOptions = newOptions.map(option => ({
    optionId: option.optionId || uuidv4(),  // Use existing optionId or generate a new one if absent
    label: option.label,                    // Label for the option
    value: option.value || option.label,    // Default value to label if value is not provided
    hint: option.hint || '',                // Default hint to an empty string if not provided
    order: option.order,                    // Order is required and should already be validated
  }));

  // Sort the formattedOptions by 'order' to maintain consistent order on updates
  const sortedOptions = formattedOptions.sort((a, b) => a.order - b.order);

//  console.log(`[updateDropdownOptions] Sorted and formatted options for optionsSetUFI '${optionsSetUFI}':`, sortedOptions);

  const optionsDocRef = doc(db, `dropdownOptions`, optionsSetUFI);

  try {
    // Update the document in Firestore with the new options, using merge to combine with existing data
    await setDoc(optionsDocRef, { options: sortedOptions }, { merge: true });
//    console.log(`[updateDropdownOptions] Successfully updated options for optionsSetUFI '${optionsSetUFI}'`);
  } catch (error) {
    console.error(`[updateDropdownOptions] Error updating options for optionsSetUFI '${optionsSetUFI}':`, error);
    throw error; // Re-throw the error for handling upstream if needed
  }
};





// Function to fetch all dropdown dependencies for a specific form
export const getDropdownDependencies = async (formId) => {
  // console.log("[getDropdownDependencies] Fetching all dependencies for formId:", formId);

  if (!formId) {
    console.error("[getDropdownDependencies] Invalid input: formId is required.");
    throw new Error("Invalid input: formId is required.");
  }

  try {
    // Reference the `dependencies` sub-collection under the specific `formId` in `dropdownDependencies`
    const dependenciesCollectionRef = collection(db, `dropdownDependencies/${formId}/dependencies`);
    
    // Fetch all documents in the `dependencies` sub-collection
    const snapshot = await getDocs(dependenciesCollectionRef);

    if (snapshot.empty) {
      console.warn("[getDropdownDependencies] No dependencies found for formId:", formId);
      return {}; // Return an empty object if no dependencies exist for this form
    }

    const allDependencies = {}; // Object to store all dependencies per optionsSetUFI
    snapshot.forEach(doc => {
      allDependencies[doc.id] = doc.data(); // Each document ID corresponds to an optionsSetUFI
    });

    // console.log("[getDropdownDependencies] Fetched all dependencies for formId:", formId, allDependencies);
    return allDependencies;

  } catch (error) {
    console.error("[getDropdownDependencies] Error fetching dependencies for formId:", formId, error);
    throw error;
  }
};





// Function to fetch dropdown dependencies for a specific optionsSetUFI within a form
export const getDropdownDependenciesForField = async (formId, optionsSetUFI) => {
  // console.log(`[getDropdownDependenciesForField] Fetching dependencies for formId: ${formId}, optionsSetUFI: ${optionsSetUFI}`);

  if (!formId || !optionsSetUFI) {
    throw new Error("FormId and optionsSetUFI are required to fetch dependencies.");
  }

  try {
    // Reference the specific document within `dependencies` for the given `optionsSetUFI`
    const dependencyDocRef = doc(db, `dropdownDependencies/${formId}/dependencies`, optionsSetUFI);

    // Fetch the document for this specific `optionsSetUFI`
    const docSnapshot = await getDoc(dependencyDocRef);

    if (!docSnapshot.exists()) {
      console.warn(`[getDropdownDependenciesForField] No dependencies found for formId: ${formId}, optionsSetUFI: ${optionsSetUFI}`);
      return {}; // Return an empty object if no dependencies exist for this `optionsSetUFI`
    }

    // Ensure dependencies for the specific `optionsSetUFI` are an object with `dependentOptionIds` as arrays
    const dependencies = docSnapshot.data();
    Object.keys(dependencies).forEach(optionId => {
      dependencies[optionId].dependentOptionIds = Array.isArray(dependencies[optionId].dependentOptionIds)
        ? dependencies[optionId].dependentOptionIds
        : [];  // Ensure `dependentOptionIds` is an array
    });

    // console.log(`[getDropdownDependenciesForField] Retrieved dependencies for formId: ${formId}, optionsSetUFI: ${optionsSetUFI}`, dependencies);
    return dependencies;

  } catch (error) {
    console.error(`[getDropdownDependenciesForField] Error fetching dependencies for formId: ${formId}, optionsSetUFI: ${optionsSetUFI}`, error);
    throw error;
  }
};



// Function to subscribe to real-time updates for dropdown dependencies for a specific form
export const subscribeToDropdownDependencies = (formId, callback) => {
  // console.log(`[subscribeToDropdownDependencies] Subscribing to dependencies for formId: ${formId}`);

  if (!formId) {
    console.error("[subscribeToDropdownDependencies] formId is required.");
    throw new Error("formId is required.");
  }

  // Reference the `dependencies` sub-collection within `dropdownDependencies/{formId}`
  const dependenciesCollectionRef = collection(db, `dropdownDependencies/${formId}/dependencies`);

  // Set up a list of unsubscribe functions for each document in the `dependencies` sub-collection
  const unsubscribeHandles = [];

  // Listen for changes to each `optionsSetUFI` within the `dependencies` collection
  const unsubscribe = onSnapshot(dependenciesCollectionRef, (snapshot) => {
    if (snapshot.empty) {
//      console.log(`[subscribeToDropdownDependencies] No dependencies found for formId: ${formId}`);
      callback({}); // Pass an empty object if no dependencies are found
      return;
    }

    const allDependencies = {};

    snapshot.docs.forEach((doc) => {
      const optionsSetUFI = doc.id;
      const dependenciesData = doc.data();

      Object.keys(dependenciesData).forEach(optionId => {
        dependenciesData[optionId].dependentOptionIds = Array.isArray(dependenciesData[optionId].dependentOptionIds)
          ? dependenciesData[optionId].dependentOptionIds
          : [];  // Ensure dependentOptionIds is an array
      });

      allDependencies[optionsSetUFI] = dependenciesData;
    });

    // Pass all updated dependencies to the callback
    callback(allDependencies);
  }, (error) => {
    console.error(`[subscribeToDropdownDependencies] Error during subscription for formId: ${formId}`, error);
    callback({}); // Handle error by passing an empty object
  });

  // Add the main unsubscribe function to the handles to stop all subscriptions
  unsubscribeHandles.push(unsubscribe);

  return () => unsubscribeHandles.forEach(unsub => unsub()); // Return a function to unsubscribe from all
};



// Function to update dropdown dependencies for a specific form
export const updateFormDropdownDependencies = async (formId, updatedDependencies) => {
  // console.log(`[updateFormDropdownDependencies] Updating dropdown dependencies for form: ${formId}`);
  if (!formId || !updatedDependencies || typeof updatedDependencies !== 'object') {
    console.error("[updateFormDropdownDependencies] formId and updatedDependencies are required.");
    throw new Error("formId and updatedDependencies are required.");
  }

  try {
    // Iterate over each optionsSetUFI in updatedDependencies to handle updates individually
    const updatePromises = Object.keys(updatedDependencies).map(async (optionsSetUFI) => {
      const optionsSetData = updatedDependencies[optionsSetUFI];

      // Format dependencies to ensure dependentOptionIds are arrays
      const formattedDependencies = Object.keys(optionsSetData).reduce((acc, optionId) => {
        const dependentOptionIds = optionsSetData[optionId]?.dependentOptionIds;

        if (Array.isArray(dependentOptionIds)) {
          acc[optionId] = { dependentOptionIds: dependentOptionIds };
        } else {
          acc[optionId] = { dependentOptionIds: [] }; // Default to an empty array if invalid
          console.warn(`[updateFormDropdownDependencies] Skipping invalid dependency for optionId: ${optionId}`);
        }
        return acc;
      }, {});

      // Reference the specific `optionsSetUFI` document within `dependencies` sub-collection
      const dependencyDocRef = doc(db, `dropdownDependencies/${formId}/dependencies`, optionsSetUFI);

      // Update the `optionsSetUFI` document with the new dependencies
      return setDoc(dependencyDocRef, formattedDependencies, { merge: true });
    });

    // Execute all updates concurrently
    await Promise.all(updatePromises);

    // console.log(`[updateFormDropdownDependencies] Successfully updated dropdown dependencies for form: ${formId}`);
    return { status: 'success', message: 'Dependencies updated successfully.' };
  } catch (error) {
    console.error(`[updateFormDropdownDependencies] Error updating dropdown dependencies for form: ${formId}; Error:`, error);
    return { status: 'error', message: 'Failed to update dependencies.', error };
  }
};



// Function to return default dropdown options with an optionsSetUFI
export const getDefaultDropdownOptions = (optionsSetUFI) => {
    const defaultOptions = {
        optionsSetUFI,  // Attach the optionsSetUFI to the options group
        options: [
            { optionId: uuidv4(), label: 'Option 1', value: 'option1', hint: 'Default Hint for Option 1', order: 1 },
            { optionId: uuidv4(), label: 'Option 2', value: 'option2', hint: 'Default Hint for Option 2', order: 2 },
            { optionId: uuidv4(), label: 'Option 3', value: 'option3', hint: 'Default Hint for Option 3', order: 3 },
        ]
    };

    return defaultOptions;
};

/**
 * Shared dropdown metadata shape builder.
 *
 * PURPOSE
 *  • Keep dropdown field metadata consistent across:
 *      - local single-select fields
 *      - local multi-select fields
 *      - semantic dropdowns like asset-type
 *      - future org-managed dropdown instances
 *
 * IMPORTANT
 *  • This helper is additive and keeps current local field behavior intact.
 *  • If embeddedOptions is not provided, it falls back to getDefaultDropdownOptions(...)
 *    so existing local dropdown creation still behaves exactly as it does today.
 *  • The returned shape matches what your current runtime expects:
 *      - optionsSetUFI
 *      - options: { optionsSetUFI, options: [...] }
 *      - dynamicOptions
 *      - multiselect
 *      - includeOther
 *      - other* fields
 *      - additionalText* fields
 *      - dependencies.triggerOptions
 */
export const buildDropdownMetadataShape = ({
    fieldType = 'single-select',
    optionsSetUFI = null,
    embeddedOptions = null,
    includeOther = false,
    displayDefaults = {},
    dependencies = { triggerOptions: {} },
}) => {
    const normalizedOptionsSetUFI = optionsSetUFI || uuidv4();
    const isMulti = fieldType === 'multi-select';

    const normalizedOptions = Array.isArray(embeddedOptions)
        ? embeddedOptions.map((opt, idx) => {
            const systemValue =
                opt.systemValue != null
                    ? String(opt.systemValue).trim()
                    : '';

            const normalizedOption = {
                optionId: opt.optionId || uuidv4(),
                label: opt.label || 'Default Label',
                value: opt.value || opt.label || '',
                hint: opt.hint || '',
                dep: opt.dep || '',
                order: Number.isFinite(Number(opt.order)) ? Number(opt.order) : (idx + 1),
            };

            // ENTERPRISE:
            // Preserve approved option-level system identity when embedded
            // defaults define it.
            //
            // This is required for system-backed dropdowns such as Asset Type,
            // where labels and values may later change but platform automation
            // still needs a stable machine meaning.
            if (systemValue) {
                normalizedOption.systemValue = systemValue;
            }

            return normalizedOption;
        })
        : getDefaultDropdownOptions(normalizedOptionsSetUFI).options;

    return {
        includeOther: includeOther === true,
        optionsSetUFI: normalizedOptionsSetUFI,
        options: {
            optionsSetUFI: normalizedOptionsSetUFI,
            options: normalizedOptions,
        },
        dynamicOptions: [],
        additionalText: '',
        multiselect: isMulti,

        otherHint: displayDefaults.otherHint || 'Please specify if other',
        otherHelperText: displayDefaults.otherHelperText || 'Provide more information for "Other"',
        otherErrorText: displayDefaults.otherErrorText || 'You must specify when selecting "Other"',
        otherMaxLength: Number.isFinite(Number(displayDefaults.otherMaxLength))
            ? Number(displayDefaults.otherMaxLength)
            : 30,

        additionalTextHint: displayDefaults.additionalTextHint || 'Please provide additional information',
        additionalTextHelperText: displayDefaults.additionalTextHelperText || 'Enter further details if necessary',
        additionalTextErrorText: displayDefaults.additionalTextErrorText || 'Additional details required',
        additionalTextMaxLength: Number.isFinite(Number(displayDefaults.additionalTextMaxLength))
            ? Number(displayDefaults.additionalTextMaxLength)
            : 30,

        dependencies: {
            triggerOptions: {
                ...((dependencies && dependencies.triggerOptions) || {}),
            },
        },
        order: 1,
    };
};

// Updated getDefaultFieldMetadata function to handle single-select and multi-select dropdown fields
export const getDefaultFieldMetadata = (fieldId, existingUFI, fieldType = 'text', existingOptionsSetUFI = null) => {
    const newUFI = existingUFI ?? uuidv4();  // ✅ Ensures incoming `ufi` is always prioritized

//    const newUFI = existingUFI || uuidv4();  // Ensure we have a UFI to associate
    const optionsSetUFI = existingOptionsSetUFI || uuidv4();  // Reuse the existing optionsSetUFI or generate a new one

    // Log the incoming fieldType to verify it's correctly identified
//    console.log(`[getDefaultFieldMetadata] Creating metadata for fieldId ${fieldId} with fieldType ${fieldType}`);

    // Create a default metadata structure keyed by fieldId
    const baseMetadata = {
        id: fieldId,
        ufi: newUFI,
        name: `Default ${
            fieldType === 'header'
                ? 'Header'
                : fieldType === 'single-select'
                ? 'Single-select'
                : fieldType === 'multi-select'
                ? 'Multi-select'
                : fieldType === 'urlField'
                ? 'URL'
                : fieldType === 'multi-line-text'
                ? 'Multi-line Text'
                : fieldType === 'date-picker'
                ? 'Date Picker'
                : fieldType === 'image'
                ? 'Image'
                : fieldType === 'asset-type'
                ? 'Asset type'
                : fieldType === 'asset-version'
                ? 'Asset version'
                : fieldType === 'read-only-text'
                ? 'Read-only text'
                : 'Text'
        } Field`,
        // Asset-type behaves as a single-select everywhere else
        // Asset-version behaves as a read-only text field everywhere else
        type:
            fieldType === 'asset-type'
                ? 'single-select'
                : fieldType === 'asset-version'
                ? 'read-only-text'
                : fieldType,

        label: `New ${
            fieldType === 'header'
                ? 'Header'
                : fieldType === 'single-select'
                ? 'Single-select'
                : fieldType === 'multi-select'
                ? 'Multi-select'
                : fieldType === 'urlField'
                ? 'URL'
                : fieldType === 'multi-line-text'
                ? 'Multi-line Text'
                : fieldType === 'date-picker'
                ? 'Date Picker'
                : fieldType === 'image'
                ? 'Image'
                : 'Text'
        } Label`,
        required: false,
        placeholder: fieldType === 'single-select' || fieldType === 'multi-select'
            ? ''
            : fieldType === 'urlField'
            ? 'Enter a URL'
            : fieldType === 'multi-line-text'
            ? 'Enter your text here...'
            : fieldType === 'date-picker'
            ? 'Select a date'
            : 'Enter some text',
        hint: 'This is a default hint',
        helperText: fieldType === 'urlField'
            ? 'Enter a valid URL (starting with http:// or https://)'
            : fieldType === 'multi-line-text'
            ? 'Enter multiple lines of text here'
            : fieldType === 'date-picker'
            ? 'Select a valid date'
            : 'Help text for field',
        externalHelperText: fieldType === 'urlField'
            ? 'Error! Ensure the URL is correct (ie starts with http:// or https://)'
            : 'Please ensure the input is correct and try again.',
        defaultValue: fieldType === 'multi-select' || fieldType === 'date-picker'
            ? []
            : '',
        
        maxLength: fieldType === 'single-select' || fieldType === 'multi-select'
            ? null
            : fieldType === 'multi-line-text'
            ? 500  // Set a higher limit for multi-line text fields
            : 50,  // Default maxLength for single-line text fields

        transformations: {
            capitalize: 'none',  // Default capitalization setting
            spacing: 'none',  // Default spacing transformation
            allowSymbols: true,
            encode: false
        },
        disabled: false,
        disabledDependentFieldId: null,
        order: 1,
        style: {
            marginLeft: '6px',
            marginRight: '6px',
            marginTop: '4px',
            marginBottom: '4px',
            paddingTop: '2px',
            paddingBottom: '4px',
            paddingLeft: '0px',
            paddingRight: '0px',
            width: 'auto'
        }
    };


    // If the field is a header, add specific header properties
    if (fieldType === 'header') {
        const headerMetadata = {
            header: 'New Header Text',
            enableSubheader: false,
            subheader: '',
            headerStyle: {
                fontSize: '24px',
                headerColor: '#000000',
                fontWeight: 'normal',
                textAlign: 'center', 
                margin: '0px 0',
                padding: '0px 0',
            },
            subheaderStyle: {
                subFontSize: '16px',
                subheaderColor: '#666666',
                subheaderFontWeight: 'normal',
                textAlign: 'center', 
                margin: '0px 0',
                padding: '0px 0',
            },
        };

        const completeHeaderMetadata = {
            [fieldId]: {
                ...baseMetadata,
                ...headerMetadata,
            }
        };

        console.log(`[getDefaultFieldMetadata] Final header metadata for fieldId ${fieldId}:`, completeHeaderMetadata);
        return completeHeaderMetadata;
    }


    // Minimal metadata for 'image' field type
    if (fieldType === 'image') {
        const imageMetadata = {
            assetReference: null, // Placeholder for the assetId (linked to AssetManager)
            allowedImageTypes: ['icon'], // Supported sub-types
            style: {
                width: '100%', // Default full-width
                height: 'auto', // Maintain aspect ratio
            },
        };

//        console.log(`[getDefaultFieldMetadata] Configured minimal metadata for image field (fieldId: ${fieldId}):`, imageMetadata);

        return {
            [fieldId]: {
                ...baseMetadata,
                ...imageMetadata,
            },
        };
    }

    // Minimal metadata for 'attach' field type (UI-only; no backend calls yet)
    if (fieldType === 'attach') {
        const attachMetadata = {
            // ── UI behavior & appearance ─────────────────────────────────────────
            displayMode: 'compact',          // 'compact' | 'preview'
            allowMultiple: false,            // allow multiple attachments in this field
            accept: ['image/*'],             // MIME groups or extensions, e.g. ['image/*', '.pdf']
            maxSizeMB: 50,                   // soft limit for UI-only validation
            showVersionBadge: true,          // show a small vN badge (e.g., 'v3') on the preview
            showVersionDrawer: true,         // clicking the badge opens a versions drawer
            showRenditionsLink: true,        // (future) surface a "View renditions" affordance
            valueShape: 'reference',         // 'reference' | 'link' (keep 'reference' for now)

            // For UI-only phase, defaultValue is null (single) or [] (if allowMultiple is later set true)
            defaultValue: null,

            // Neutralize text-only defaults inherited from baseMetadata
            maxLength: null,

            // Optional UI tuning knobs (consistent with your style usage)
            ui: {
                thumbnailSize: 'md',          // 'sm' | 'md' | 'lg'
                dropzoneHeight: '180px',      // used in preview mode
                compactButtonHeight: 32,   // default visual height for the compact button
               compactButtonWidth: 140,      // px (numeric default; aligns with component)
               // badgePosition intentionally removed (unused in AttachFieldComponent)

            },

            // Override text-facing properties for better UX out-of-the-box
            name: 'Default Attach Field',
            label: 'Attach File',
            placeholder: 'Drag & drop or select a file',
            helperText: 'Attach a file. You can replace it later.',
            externalHelperText: 'Please attach a valid file.',
        };

        //        console.log(`[getDefaultFieldMetadata] Configured minimal metadata for attach field (fieldId: ${fieldId}):`, attachMetadata);

        return {
            [fieldId]: {
                ...baseMetadata,
                ...attachMetadata,
            },
        };
    }


    // System field: Asset Version (read-only)
    // - This is a palette/system field for AUF and DAM surfaces.
    // - It renders via ReadOnlyTextFieldComponent in FieldRenderer.
    // - It computes its value at runtime from the AttachFieldComponent answer
    //   (typically { assetId, versionNumber }).
    if (fieldType === 'asset-version' || fieldType === 'read-only-text') {
        const readOnlyMetadata = {
            // Explicit system semantics (FieldRenderer will use these)
            readOnly: true,
            isReadOnly: true,
            systemKey: 'asset.versionNumber',

            // Optional: explicitly bind to a specific attach UFI later if needed
            // sourceUfi: null,

            // Override generic defaults so you don't see "New Text Label"
            name: 'Asset version field',
            label: 'Version #',
            hint: 'Auto-fill from asset',
            helperText: '(Read only) from attachment.',
            externalHelperText: '',

            required: false,
            placeholder: '',
            defaultValue: '',

            // Prevent text-field assumptions
            maxLength: null,

            // Neutral transformations (display only)
            transformations: {
                capitalize: 'none',
                spacing: 'none',
                allowSymbols: true,
                encode: false
            },
        };

        return {
            [fieldId]: {
                ...baseMetadata,
                ...readOnlyMetadata,
                // Runtime type is enforced in baseMetadata.type normalization above,
                // but we also enforce it here defensively.
                type: 'read-only-text',
                semanticKey: 'asset.versionNumber',
            },
        };
    }

    // If the field is an Asset Type dropdown, specialize the defaults
    // while still behaving as a normal single-select in the rest of the app.
    if (fieldType === 'asset-type') {
      // Default asset type choices – you can refine this list later
      //
      // ENTERPRISE:
      // `systemValue` is the durable platform-owned machine meaning.
      //
      // Keep all three identities distinct:
      //   • optionId    = answer/dependency identity
      //   • label       = admin/user-facing display text
      //   • value       = existing form/export value behavior
      //   • systemValue = internal asset type meaning used by catalog/filtering
      const assetTypeOptions = [
        { label: 'Banner',       value: 'banner',       systemValue: 'banner' },
        { label: 'Document',     value: 'document',     systemValue: 'document' },
        { label: 'Graphic',      value: 'graphic',      systemValue: 'graphic' },
        { label: 'Icon',         value: 'icon',         systemValue: 'icon' },
        { label: 'Illustration', value: 'illustration', systemValue: 'illustration' },
        { label: 'Logo',         value: 'logo',         systemValue: 'logo' },
        { label: 'Photo',        value: 'photo',        systemValue: 'photo' },
        { label: 'Screenshot',   value: 'screenshot',   systemValue: 'screenshot' },
        { label: 'Video',        value: 'video',        systemValue: 'video' },
      ].map((opt, idx) => ({
        optionId: uuidv4(),
        label   : opt.label,
        value   : opt.value,
        systemValue: opt.systemValue,
        hint    : '',
        dep     : '',
        order   : idx + 1,
      }));

      const dropdownMetadata = {
        ...buildDropdownMetadataShape({
          fieldType: 'single-select',
          optionsSetUFI,
          embeddedOptions: assetTypeOptions,
          includeOther: false,
          displayDefaults: {
            otherHint: 'Please specify if other',
            otherHelperText: 'Provide more information for "Other"',
            otherErrorText: 'You must specify when selecting "Other"',
            otherMaxLength: 30,
            additionalTextHint: 'Please provide additional information',
            additionalTextHelperText: 'Enter further details if necessary',
            additionalTextErrorText: 'Additional details required',
            additionalTextMaxLength: 30,
          },
          dependencies: {
            triggerOptions: {},
          },
        }),

        // 🔑 DAM role flag – lets ingest/registry logic know
        // that THIS dropdown is the canonical asset type field.
        assetFieldRole: 'assetType',
      };

      return {
        [fieldId]: {
          ...baseMetadata,
          ...dropdownMetadata,
          // 🆕 semantic key: this field edits the canonical asset.type
          semanticKey: 'asset.type',

          // Override some generic defaults with DAM-specific ones
          name    : 'Asset type field',
          label   : 'Asset type',
          hint    : 'Select the asset type used for DAM filters',
          required: true,
        },
      };
    }

    // If the field is a single-select or multi-select dropdown, add specific dropdown properties
    if (fieldType === 'single-select' || fieldType === 'multi-select') {
      const dropdownMetadata = buildDropdownMetadataShape({
        fieldType,
        optionsSetUFI,
        includeOther: false,
        displayDefaults: {
          otherHint: 'Please specify if other',
          otherHelperText: 'Provide more information for "Other"',
          otherErrorText: 'You must specify when selecting "Other"',
          otherMaxLength: 30,
          additionalTextHint: 'Please provide additional information',
          additionalTextHelperText: 'Enter further details if necessary',
          additionalTextErrorText: 'Additional details required',
          additionalTextMaxLength: 30,
        },
        dependencies: {
          triggerOptions: {},
        },
      });

      return {
        [fieldId]: {
          ...baseMetadata,
          ...dropdownMetadata,
        },
      };
    }

    // If the field is a multi-line text field, add specific multi-line properties
    if (fieldType === 'multi-line-text') {
        const multiLineMetadata = {
            multiline: true, // Always true for multi-line text fields
            lineCount: 3,  // Use lineCount instead of rows, default to 1
        };

        const multiLineMetadataComplete = { ...baseMetadata, ...multiLineMetadata };
//        console.log(`[getDefaultFieldMetadata] Final multi-line text metadata for fieldId ${fieldId}:`, multiLineMetadataComplete);

        return {
            [fieldId]: multiLineMetadataComplete
        };
    }

// Date picker metadata
if (fieldType === 'date-picker') {
    const datePickerMetadata = {
        dateFormat: 'MM/DD/YYYY',  // Default format for displaying dates
        dateMode: 'single',        // Default to single date selection
        defaultValue: '',          // Default to an empty string
        transformations: {
            capitalize: 'none',    // Capitalization setting for month names if applicable
            spacing: '/',          // Default to a separator for dates
            encode: false
        },
        helperText: 'Select a date in the specified format',  // Helper text specific to date picker
        externalHelperText: 'Invalid date. Please check your input.',  // Custom error message for invalid dates
        placeholder: 'MM/DD/YYYY', // Placeholder aligned with the default format
    };

    const datePickerMetadataComplete = { ...baseMetadata, ...datePickerMetadata };
//    console.log(`[getDefaultFieldMetadata] Final date-picker metadata for fieldId ${fieldId}:`, datePickerMetadataComplete);

    return {
        [fieldId]: datePickerMetadataComplete
    };
}

//   console.log(`[getDefaultFieldMetadata] Final base metadata for fieldId ${fieldId}:`, baseMetadata);

    /******************* CALCULATED FIELD ************************/
    if (fieldType === FIELD_TYPE_CALCULATED || fieldType === 'calculated') {
        const calcMetadata = {
            readOnly : true,          // cannot be edited on the live form
            formula  : 'CONCAT',      // future-proof: ADD, FORMAT_DATE, …
            fields   : [],            // empty ⇒ concat all non-submit answers
            delimiter: ' ',
            template : '', 
        };

        return {
            [fieldId]: {
                ...baseMetadata,
                ...calcMetadata,
                label : 'Submitted data',   // designer can rename later
                hint  : '',
                helperText: '',
            },
        };
    }
    /*************************************************************/

    return {
        [fieldId]: baseMetadata
    };
};




// Function to fetch field metadata based on a form's layout configuration
export const getFieldMetadata = async (formId) => {
//    console.log("[utils: getFieldMetadata] Called with formId:", formId);

    if (!formId) {
        console.error("[utils: getFieldMetadata] Called without a formId.");
        return { status: 'error', message: 'No form ID provided.', data: {} };
    }

    try {
        const formRef = doc(db, 'forms', formId);
        const formSnap = await getDoc(formRef);

        if (!formSnap.exists()) {
//            console.log(`[utils: getFieldMetadata] No form found with ID: ${formId}. Assuming new form and using default metadata.`);
            
            // Call getDefaultLayout to generate the layout and default metadata
            const layout = getDefaultLayout();  // This will generate fieldId and ufi

            // Extract metadata from the generated layout
            const fieldMetadata = {};
            layout.row0.forEach(field => {
                fieldMetadata[field.id] = getDefaultFieldMetadata(field.id, field.ufi)[field.id];
            });

            return {
                status: 'success',
                message: 'Using default metadata for new form.',
                data: fieldMetadata  // Return generated default metadata
            };
        }

        // If form exists, process as normal
        // Extract the layout from the Firestore document
        const layout = formSnap.data().layoutConfig || {};  // Get layoutConfig from Firestore data

        // Extract field IDs from the layout, skipping submit buttons
        const fieldIDs = layout.rows?.flatMap(row => row.fields)
            .filter(field => field.type !== 'submit' && field.id)  // Filter out submit buttons
            .filter(Boolean);  // Filter out any falsy values

//        console.log("[utils: getFieldMetadata] Extracted fieldIDs (excluding submit buttons):", fieldIDs);

        const fieldMetadata = {};
        const promises = fieldIDs.map(fieldID => getDoc(doc(db, 'fieldMetadata', fieldID)));  // Fetch metadata from Firestore
        const fieldDocs = await Promise.all(promises);

        fieldDocs.forEach((docSnap, index) => {
            const fieldID = fieldIDs[index];

            if (docSnap.exists()) {
                const fieldData = docSnap.data();

                // Fetch UFI, use fieldData's UFI if available or fallback to the fieldID
                const ufi = fieldData.ufi || fieldID;
                const defaultMetadata = getDefaultFieldMetadata(fieldID, ufi)[fieldID] || {};  // Fetch metadata by fieldID and UFI

                // Merge the default metadata with the fetched data from Firestore
                fieldMetadata[fieldID] = {
                    ...defaultMetadata,  // Default metadata structure
                    ...fieldData,  // Override defaults with Firestore data
                    style: {
                        ...defaultMetadata.style,  // Apply default styles
                        ...fieldData.style  // Override with Firestore styles
                    }
                };

                   // Header and Subheader Handling
            if (fieldData.type === 'header') {
                const defaultHeaderMetadata = getDefaultFieldMetadata(fieldID, ufi, 'header')[fieldID];

                // Ensure header-specific properties are properly merged
                fieldMetadata[fieldID] = {
                    ...fieldMetadata[fieldID],  // Retain merged metadata
                    headerStyle: {
                        ...defaultHeaderMetadata.headerStyle,
                        ...fieldData.headerStyle
                    },
                    subheaderStyle: {
                        ...defaultHeaderMetadata.subheaderStyle,
                        ...fieldData.subheaderStyle
                    }
                };

                // Handle potential missing attributes with improved logic
                fieldMetadata[fieldID].header = fieldMetadata[fieldID].header || "New Header Text";

                // Ensure `enableSubheader` properly retains `false` values
                fieldMetadata[fieldID].enableSubheader = 
                    fieldMetadata[fieldID].enableSubheader !== undefined
                        ? fieldMetadata[fieldID].enableSubheader
                        : false;

                fieldMetadata[fieldID].subheader = fieldMetadata[fieldID].subheader || "";

                // Handle text alignment defaults if missing
                fieldMetadata[fieldID].headerStyle.textAlign = 
                    fieldMetadata[fieldID].headerStyle.textAlign !== undefined
                        ? fieldMetadata[fieldID].headerStyle.textAlign
                        : "center";

                fieldMetadata[fieldID].subheaderStyle.textAlign = 
                    fieldMetadata[fieldID].subheaderStyle.textAlign !== undefined
                        ? fieldMetadata[fieldID].subheaderStyle.textAlign
                        : "center";
            }


            //                if (fieldData.type === 'date-picker') {
            //                    console.log(`[utils: getFieldMetadata] Loaded metadata for date-picker field with ID ${fieldID}`);
            //                }

            //                console.log(`[utils: getFieldMetadata] Metadata for fieldID ${fieldID} found and merged with defaults. UFI: ${ufi}`);
                        } else {
                            console.warn(`[utils: getFieldMetadata] Field metadata for field ID ${fieldID} not found in Firestore, using default`);

                            // Generate a default UFI based on fieldID, or fallback to a new UFI if needed
                            const existingUFI = layout.rows?.flatMap(row => row.fields).find(field => field.id === fieldID)?.ufi || null;

                            // Use default metadata with the existing or generated UFI
                            fieldMetadata[fieldID] = getDefaultFieldMetadata(fieldID, existingUFI)[fieldID] || {};
            //                if (fieldMetadata[fieldID].type === 'date-picker') {
            //                    console.log(`[utils: getFieldMetadata] Using default metadata for date-picker field with ID ${fieldID}`);
            //                }
                        
                        }
                    });

                    return { status: 'success', message: 'Field metadata fetched successfully.', data: fieldMetadata };
                } catch (error) {
                    console.error("[utils: getFieldMetadata] Error fetching field metadata:", error);
                    return { status: 'error', message: 'Error fetching field metadata.', error };
                }
            };





// Function to fetch metadata for a single field based on its field ID (ufi)
export const getFieldMetadataForSingleField = async (fieldId) => {
    if (!fieldId) {
        console.error("[utils: getFieldMetadataForSingleField] Called without a fieldId.");
        return { status: 'error', message: 'No field ID provided.' };
    }

    const fieldRef = doc(db, "fieldMetadata", fieldId);

    try {
        const docSnap = await getDoc(fieldRef);

        if (docSnap.exists()) {
            const fieldData = docSnap.data();

            // Handle header-specific logic with updated schema
            if (fieldData.type === 'header') {
                const defaultHeaderMetadata = getDefaultFieldMetadata(fieldId, fieldData.ufi, 'header')[fieldId];

                // Ensure header-specific properties are properly merged
                fieldData.headerStyle = {
                    ...defaultHeaderMetadata.headerStyle,
                    ...fieldData.headerStyle
                };

                fieldData.subheaderStyle = {
                    ...defaultHeaderMetadata.subheaderStyle,
                    ...fieldData.subheaderStyle
                };

                // Handle potential missing attributes with improved logic
                fieldData.header = fieldData.header || "New Header Text";

                // Ensure `enableSubheader` properly retains `false` values
                fieldData.enableSubheader = 
                    fieldData.enableSubheader !== undefined
                        ? fieldData.enableSubheader
                        : false;

                fieldData.subheader = fieldData.subheader || "";

                // Handle text alignment defaults if missing
                fieldData.headerStyle.textAlign = 
                    fieldData.headerStyle.textAlign !== undefined
                        ? fieldData.headerStyle.textAlign
                        : "center";

                fieldData.subheaderStyle.textAlign = 
                    fieldData.subheaderStyle.textAlign !== undefined
                        ? fieldData.subheaderStyle.textAlign
                        : "center";
            }

            return { status: 'success', data: fieldData };
        } else {
            console.warn(`[utils: getFieldMetadataForSingleField] No metadata found for field ID: ${fieldId}`);
            return { status: 'not_found', data: null };
        }
    } catch (error) {
        console.error(`[utils: getFieldMetadataForSingleField] Error fetching metadata for field ID: ${fieldId}`, error);
        return { status: 'error', error };
    }
};



// Function to provide a default layout configuration with a static submit button
export const getDefaultLayout = (alignment = 'center', existingUFI = null) => {
//    console.log('[getDefaultLayout] Generating default layout...');  // <-- Add this log

    const defaultFieldId = uuidv4();  // Generate a unique ID for the default text field
    const submitButtonId = uuidv4();  // Generate a unique ID for the submit button
    const ufi = existingUFI || uuidv4();  // Use the existing UFI or generate a new one

//    console.log(`[utils: getDefaultLayout] Generated defaultFieldId: ${defaultFieldId}`);
//    console.log(`[utils: getDefaultLayout] Generated submitButtonId: ${submitButtonId}`);

    // Get the default field metadata for the generated field ID and UFI
    const defaultFieldMetadata = getDefaultFieldMetadata(defaultFieldId, ufi);  // Pass the field ID and UFI

    // Log the metadata to ensure correctness
//    console.log(`[utils: getDefaultLayout] Retrieved default metadata for defaultFieldId: ${defaultFieldId}`, defaultFieldMetadata);

    // Construct the layout configuration with detailed logging
    const layoutConfig = {
        row0: [ // Default text field in the first row
            {
                ...defaultFieldMetadata[defaultFieldId],  // Use metadata keyed by fieldId
                id: defaultFieldId,  // Ensure the ID is set
                ufi: ufi,
                type: 'text',  // Explicitly set field type
                index: 0  // Ensure the index is set correctly
            }
        ],
        submitRow: [ // Submit button in a dedicated row
            {
                id: submitButtonId,
                type: 'submit',
                label: 'Submit',
                actions: [
                    { type: 'displayMessage', message: 'Thank you for your submission!' }
                ],
                style: {
                    alignment: alignment,
                    backgroundColor: '#4285f4',
                    textColor: '#FFFFFF',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    height: '50px',
                    width: '150px',
                    borderRadius: '5px'
                },
                index: 1
            }
        ]
    };

    // Log the final layout configuration
//   console.log('[utils: getDefaultLayout] Final layout configuration:', layoutConfig);

    return layoutConfig;
};









// Function to fetch the layout configuration for a specific form
export const getLayoutConfig = async (formId, options = {}) => {
//    console.log(`[utils: getLayoutConfig] Called with formId: ${formId}`);

    if (!formId) {
        console.error("[utils: getLayoutConfig] No formId provided.");
        return { status: 'error', message: 'No form ID provided.', layout: null, metadata: null };
    }

    try {
        const formRef = doc(db, 'forms', formId); // Reference to the Firestore document
        const formSnap = await getDoc(formRef); // Fetch the document from Firestore

        let layout;
        let fetchedFromFirestore = false; // Flag to track if the layout was fetched from Firestore

        //  prefer draftOverlay.layoutConfig when present (enterprise working copy)
        // - draftOverlay is written by saveDraftFormToFirestore(saveMode='draftOverlay')
        // - layoutConfig may be a compressed Base64 string OR an object; caller already handles decompression later
        const formData = formSnap.exists() ? (formSnap.data() || {}) : {};

        //  (resolver): centralizes overlay vs root selection
        // mode:'auto' matches your current behavior:
        //   overlay when active + present, else root layoutConfig
        const versionSnap = getFormVersionSnapshot(formData);

        // Use caller-selected mode (default: 'auto')
        // - options.viewMode is UI-facing: 'auto' | 'draft' | 'published'
        // - options.mode is resolver-facing: 'auto' | 'overlay' | 'root' | 'published' | 'draft'
        // If both are provided, options.mode wins (surgical override for callers that already pass mode)
        const viewMode = options?.viewMode || null;
        const resolverModeFromView = mapViewModeToResolverMode(viewMode); // ← from formVersionResolver.js

        const mode = options?.mode || resolverModeFromView || 'auto';

        // Optional: make it obvious in logs what the caller asked for
        console.log('[utils: getLayoutConfig] view/mode input:', { viewMode, resolverModeFromView, mode });

        const resolvedLayout = resolveLayoutConfig(formData, { mode, debug: true });



        // Optional debug log (keep commented unless you want it noisy)
         console.log('[utils: getLayoutConfig] version snapshot:', versionSnap);
         console.log('[utils: getLayoutConfig] resolved layout source:', resolvedLayout);

        if (formSnap.exists() && resolvedLayout?.value != null) {
             console.log("[utils: getLayoutConfig] Using resolved layout from Firestore:", resolvedLayout.path);
            layout = resolvedLayout.value;
            fetchedFromFirestore = true;
        } else if (formSnap.exists() && formData.layoutConfig) {
            // NOTE: This branch is a conservative fallback. In practice, resolvedLayout will already have used root.
            // console.log("[utils: getLayoutConfig] Form data fetched successfully from Firestore.");
            layout = formData.layoutConfig;
            fetchedFromFirestore = true;
        } else {
            // If no layout is found, use the default layout
            // console.log("[utils: getLayoutConfig] No layout found in Firestore, providing default layout.");
            layout = getDefaultLayout();  // Generate new field metadata for new forms
        }


        // Ensure each field in the layout has its metadata set, reusing existing UFIs
        const fieldsMetadata = {};

        //  NEW: Guard — layoutConfig can be a compressed Base64 string.
        // In that case, we cannot safely iterate through it here.
        // The caller (FormLayoutContext.fetchLatestLayout) already handles decompression.
        if (layout && typeof layout === 'object') {
            Object.values(layout).flat().forEach(field => {
                if (field.type !== 'submit') { // Skip submit button metadata
                    const fieldId = field.id || uuidv4();  // Ensure fieldId is valid, generate if missing
                    const ufi = field.ufi || fieldId;  // Reuse UFI if available, otherwise use fieldId

                    // Fetch metadata based on fieldId and UFI
                    const defaultMetadata = getDefaultFieldMetadata(fieldId, ufi);  // No need to access [fieldId]

                    // Merge the default metadata with the field's data
                    fieldsMetadata[fieldId] = {
                        ...defaultMetadata, // Default metadata structure
                        ...field, // Override defaults with Firestore field data
                        style: {
                            ...defaultMetadata.style, // Apply default styles
                            ...field.style // Override with Firestore styles
                        }
                    };

//                    console.log(`[utils: getLayoutConfig] Metadata for fieldId ${fieldId} retrieved and merged with defaults.`);
                }
            });
        } else {
//            console.log("[utils: getLayoutConfig] layoutConfig is compressed or missing; skipping inline fieldsMetadata build.");
        }

        // Return the layout and metadata
        return {
            status: fetchedFromFirestore ? 'success' : 'default',
            message: fetchedFromFirestore ? 'Layout and metadata fetched successfully.' : 'Default layout provided.',
            layout,
            //  NEW: metadata can still be sourced from root doc even when using overlay
            metadata: { isPublic: formData?.isPublic, isTemplate: formData?.isTemplate },
            fieldsMetadata,
        };
    } catch (error) {
        console.error("[utils: getLayoutConfig] Error fetching layout configuration:", error);
        return { status: 'error', message: 'Error fetching layout configuration.', error, layout: null, metadata: null };
    }
};










// Function to selectively serialize layout configuration focusing on minimal data necessary for UI reconstruction
export const serializeLayoutConfig = (layoutConfig) => {
    const seen = new WeakSet();
    
    return JSON.stringify(layoutConfig, (key, value) => {
        if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
                return; // Avoid circular references
            }
            seen.add(value);

            // Customize serialization to include only essential data for rebuilding the layout
            if (value && typeof value === 'object' && value.id && value.ufi) {  // Ensure UFI is used
                // Only serialize the minimal properties necessary for rebuilding the layout
                return {
                    id: value.id,
                    ufi: value.ufi,  // Ensure UFI is serialized for identity management
                    type: value.type,  // Keep type for UI rendering
                    label: value.label,  // Often used for display purposes
                    placeholder: value.placeholder,  // For inputs
                    required: value.required,  // For validating inputs
                    index: value.index,  // For order or specific placements in the layout
                    style: value.style,  // Include style properties for rendering
                    // Include other properties as needed based on your component's usage
                };
            }
        }
        return value;
    });
};



// Function to save the layout configuration for a specific form
export const saveLayoutConfig = async (formId, layoutConfig, additionalMetadata = {}) => {
//    console.log(`[saveLayoutConfig] Saving layout configuration for form ID: ${formId}`);

    const currentUser = auth.currentUser;
    if (!formId || !layoutConfig || !currentUser) {
        console.error("[saveLayoutConfig] Missing necessary parameters.");
        return { status: 'error', message: 'Form ID, layout configuration, and user authentication are required.' };
    }

    // Serialize layoutConfig here
    let cleanLayoutConfig;
    try {
        cleanLayoutConfig = JSON.parse(serializeLayoutConfig(layoutConfig));
    } catch (error) {
        console.error("[saveLayoutConfig] Error serializing layout configuration:", error);
        return { status: 'error', message: 'Failed to serialize layout configuration.' };
    }

    try {
        const formRef = doc(db, 'forms', formId);
        const formSnap = await getDoc(formRef);

        // Include the clean layout config and additional metadata
        const metadata = {
            layoutConfig: cleanLayoutConfig,
            lastModified: serverTimestamp(),
            lastModifiedBy: currentUser.uid,
            ...additionalMetadata,
        };

        if (!formSnap.exists()) {
//            console.log(`[saveLayoutConfig] Creating new form with layout configuration for form ID: ${formId}.`);
            await setDoc(formRef, {
                ...metadata,
                createdBy: currentUser.uid,
                createdAt: serverTimestamp(),
            });
            return { status: 'success', message: 'New form created with layout configuration and additional metadata.' };
        } else {
//            console.log(`[saveLayoutConfig] Updating existing form with layout configuration for form ID: ${formId}.`);
            await updateDoc(formRef, metadata);
            return { status: 'success', message: 'Layout configuration and additional metadata updated successfully.' };
        }
    } catch (error) {
        console.error("[saveLayoutConfig] Error saving layout configuration:", error);
        return { status: 'error', message: 'Error saving layout configuration.', error };
    }
};



// Function to update or create field metadata in Firestore
export const updateFieldInFirestore = async (fieldId, changes) => {
//  console.log(`Attempting to update or create field ${fieldId} with changes`, changes);
  const fieldRef = doc(db, "fieldMetadata", fieldId);

  try {
    await setDoc(fieldRef, changes, { merge: true }); // Automatically handles create/update
//    console.log(`Field ${fieldId} updated or created successfully.`);
  } catch (error) {
    console.error(`Failed to update or create field ${fieldId}:`, error);
    throw error; // rethrow to handle it in the calling context
  }
};


// Simplified function to add form details (including the screenshot URL) to Firestore
export const addFormWithScreenshotToFirestore = async (formDetails) => {
  try {
    const formRef = await addDoc(collection(db, 'forms'), {
      ...formDetails,
      createdAt: new Date(),
      lastModified: new Date(),
    });
//    console.log(`Form added with ID: ${formRef.id}`);
    return { status: 'success', message: 'Form successfully added.', id: formRef.id };
  } catch (error) {
    console.error('Error adding form to Firestore:', error);
    return { status: 'error', message: 'Error adding form to Firestore.', error };
  }
};

// Function to save a new form with permission check
export const saveFormIfAuthorized = async (userId, formDetails) => {
  const hasPermission = await checkUserPermission(userId, 'createForms');
  if (!hasPermission) {
    throw new Error('You do not have permission to create forms.');
  }

  try {
    const formRef = await addDoc(collection(db, 'forms'), {
      ...formDetails,
      createdBy: userId,
      createdAt: new Date(),
      lastModified: new Date(),
      isPublic  : false,
      orgAccess : 'none',   // 'none' | 'view' | 'comment' | 'edit'
      sharedWith: {},       // { uid : 'view' | 'comment' | 'edit' }
      });
//    console.log(`Form added with ID: ${formRef.id}`);
    return { status: 'success', message: 'Form successfully added.', id: formRef.id };
  } catch (error) {
    console.error('Error adding form to Firestore:', error);
    return { status: 'error', message: 'Error adding form to Firestore.', error };
  }
};

// Function to save a form as a draft to Firestore
export const saveDraftFormToFirestore = async ({
  formId,
  compressedDraftData,
  compressedLayoutConfig,
  compressedThemeData,
  compressedFieldsMetadata, 
  compressedDropdownOptions,
  compressedDropdownDependencies,
  compressedSubmissionSettings, 
//  compressedSubmitStatus, 
  userId,
  organizationId,
  formDetails,

  // enterprise “working copy” behavior
  // - 'draft'        => writes to root doc as draft (legacy behavior)
  // - 'draftOverlay' => writes a draft overlay while keeping published doc published
  saveMode = 'draftOverlay',

  /* ───────────────────────────────────────────────
     Optional: form-scoped ingest mapping so admins
        can test the UFI mapping while in draft.
        (Same shape used on publish.)
     ─────────────────────────────────────────────── */
  assetIngestConfig,  // ← (optional)
}) => {

  
//  console.log(`[utils/firestore] Attempting to save draft form with ID: ${formId} for user: ${userId}...`);
  console.log('[utils/firestore] Function called with:', {
    formId,
    userId,
    organizationId,
    formDetails,
    compressedFieldsMetadata,
    compressedDropdownOptions,
    compressedDropdownDependencies,
    compressedSubmissionSettings, //  Log new data
//    compressedSubmitStatus //  Log new data
    //  Visibility for ingest config in drafts (safe to log shape only)
    hasAssetIngestConfig: !!assetIngestConfig,
  });

  if (!formDetails || !formDetails.title || !formDetails.description) {
    console.error('[utils/firestore] ERROR: formDetails is missing title or description upon function entry!');
  }
  if (typeof formDetails !== 'object' || !formDetails?.title || !formDetails?.description) {
    console.error('[utils/firestore] ❌ ERROR: formDetails is malformed or missing:', formDetails);
  }

  try {
    // Reference the form document in Firestore
    const formRef = doc(db, 'forms', formId);

    // read existing doc so we can decide draft vs overlay behavior safely
    const existingSnap = await getDoc(formRef);
    const existingData = existingSnap.exists() ? (existingSnap.data() || {}) : {};
    const existingStatus = existingData?.status || 'none';

    console.log('[utils/firestore] saveDraftFormToFirestore saveMode/status snapshot:', {
      formId,
      saveMode,
      existingStatus,
      existingIsAssetUploadForm: existingData?.isAssetUploadForm === true,
    });

    // When saving an overlay on a published form, keep the published doc published.
    const shouldWriteOverlay =
      saveMode === 'draftOverlay' &&
      existingSnap.exists() &&
      existingStatus === 'published';

// ADD: prove which branch should run
console.log('[utils/firestore] saveDraftFormToFirestore branch decision:', {
  formId,
  saveMode,
  existingStatus,
  exists: existingSnap.exists(),
  shouldWriteOverlay,
  rootHasOverlayObject: !!existingData?.draftOverlay,
  rootDraftOverlayStatus: existingData?.draftOverlayStatus || null,
  overlayStatus: existingData?.draftOverlay?.status || null,
});



    // ─────────────────────────────────────────────────────────────
    // Guardrails for “this draft is the Asset Upload Form”
    //   • If admin is providing an assetIngestConfig for this draft,
    //     ensure the marker is present (and org id is set).
    //   • This keeps identity explicit *before* publish.
    // ─────────────────────────────────────────────────────────────
    const draftIsAssetUploadForm =
      formDetails?.isAssetUploadForm === true ||
      Boolean(assetIngestConfig); // infer from presence of mapping

    if (assetIngestConfig && !organizationId) {
      console.warn('[utils/firestore] assetIngestConfig provided but organizationId is missing on draft — ingestion mapping will be saved, but org-scoped features may not behave as expected until orgId is set.');
    }

//    console.log(`[utils/firestore] title: ${formDetails?.title}`);
//    console.log(`[utils/firestore] description: ${formDetails?.description}`);
//    console.log(`[utils/firestore] organizationId: ${organizationId}`);
    
    // ✅ NEW: overlay vs draft behavior
    // - If saveMode === 'draftOverlay' AND the existing form is published,
    //   write a draft overlay without changing the published form's status.
    // - Otherwise, keep the current legacy behavior (write status:'draft' on the root doc).
    //
    // NOTE: This assumes you've already added Step 2 above this block:
    //   const existingSnap = await getDoc(formRef);
    //   const existingData = existingSnap.exists() ? (existingSnap.data() || {}) : {};
    //   const existingStatus = existingData?.status || 'none';
    //   const shouldWriteOverlay = saveMode === 'draftOverlay' && existingSnap.exists() && existingStatus === 'published';

    if (shouldWriteOverlay) {
      console.log('[utils/firestore] Saving DRAFT OVERLAY (keeping published doc published):', {
        formId,
        userId,
        organizationId,
        existingStatus,
        saveMode,
      });

      // ✅ Draft overlay lives alongside the published doc without changing `status`
      await setDoc(
        formRef,
        {
          // ─────────────────────────────────────────────────────────────
          // Draft overlay payload: uses the same blobs but stores them under
          // a separate namespace so published remains source-of-truth until publish.
          // ─────────────────────────────────────────────────────────────
          draftOverlay: {
            compressedData: compressedDraftData, // Store compressed overlay data in a single field
            layoutConfig: compressedLayoutConfig, // Store compressed layoutConfig separately
            theme: compressedThemeData, // ✅ Store compressed theme data separately
            fieldsMetadata: compressedFieldsMetadata,
            dropdownOptions: compressedDropdownOptions,
            dropdownDependencies: compressedDropdownDependencies,
            submissionSettings: compressedSubmissionSettings,
            // submitStatus: compressedSubmitStatus,

            status: 'draftOverlay', // Mark THIS payload as a draft overlay (not the root form status)
            savedBy: userId, // Track who saved the overlay
            savedAt: serverTimestamp(), // Track when the overlay was saved
            lastModified: serverTimestamp(),
            lastModifiedBy: userId, // Track who last modified the overlay

            organizationId: organizationId || '', // Ensure the organizationId is included correctly

            // Keep title/description consistent in builder UX without overwriting published values
            title: formDetails?.title || existingData?.title || '', // Ensure title is saved separately
            description: formDetails?.description || existingData?.description || '', // Ensure description is saved separately

            createdBy: formDetails?.createdBy || existingData?.createdBy || userId,
            owner: formDetails?.owner || existingData?.owner || userId,
            isTemplate: formDetails?.isTemplate ?? existingData?.isTemplate ?? false,

            // Keep sharing flags snapshot for editor UX (DO NOT change published flags here)
            isPublic: formDetails?.isPublic ?? existingData?.isPublic ?? false,
            orgAccess: formDetails?.orgAccess ?? existingData?.orgAccess ?? 'none',
            sharedWith: formDetails?.sharedWith ?? existingData?.sharedWith ?? {},

            // ─────────────────────────────────────────────────────────────
            // Draft marker: if this form is being configured as the org’s
            //    Asset Upload Form, set the flag now (explicit, discoverable).
            //    This does not change publish visibility; publish still enforces
            //    isPublic:false and orgAccess:'view'.
            // ─────────────────────────────────────────────────────────────
            ...(draftIsAssetUploadForm ? { isAssetUploadForm: true } : {}),

            // ─────────────────────────────────────────────────────────────
            // Form-scoped ingest mapping (draft overlay): which UFIs represent
            //    identity/routing (canonical name, type, status, tag UFIs).
            //    Including it in overlays lets admins test ingestion before publish.
            //    Only attach when provided; keeps schema additive/flexible.
            // ─────────────────────────────────────────────────────────────
            ...(assetIngestConfig && typeof assetIngestConfig === 'object'
              ? {
                  assetIngestConfig: {
                    canonicalNameUfi : assetIngestConfig.canonicalNameUfi ?? null,
                    assetTypeUfi     : assetIngestConfig.assetTypeUfi     ?? null,
                    statusUfi        : assetIngestConfig.statusUfi        ?? null,
                    tagUfis          : Array.isArray(assetIngestConfig.tagUfis)
                                       ? assetIngestConfig.tagUfis : [],
                    collectionUfi    : assetIngestConfig.collectionUfi    ?? null,
                  }
                }
              : {}),

            // Optional fields based on provided data
            submitButton: compressedDraftData?.submitButton || null, // Retain submit button configuration if applicable
            fieldChanges: compressedDraftData?.fieldChanges || null, // Field changes if provided
            // submissionSettings: compressedDraftData?.submissionSettings || null, // Submission settings if provided
          },

          // ─────────────────────────────────────────────────────────────
          // Root-level overlay index fields (query-friendly)
          // ─────────────────────────────────────────────────────────────
          draftOverlayStatus: 'draftOverlay',
          draftOverlaySavedAt: serverTimestamp(),
          draftOverlaySavedBy: userId,
        },
        { merge: true }
      );

      // console.log(`[utils/firestore] Draft overlay for form ${formId} saved successfully.`);
      return { status: 'success', message: 'Draft overlay saved successfully.' };
    }

    // ─────────────────────────────────────────────────────────────
    // Legacy behavior (your existing logic): save as draft on root doc
    // ─────────────────────────────────────────────────────────────

    // ✅ CHANGE: do NOT refuse saving when saveMode=draftOverlay but the form isn't published.
    // In that case, we intentionally fall back to legacy root-draft behavior.
    if (saveMode === 'draftOverlay' && !shouldWriteOverlay) {
      console.warn('[utils/firestore] saveMode=draftOverlay but overlay is not applicable; falling back to ROOT DRAFT save.', {
        formId,
        saveMode,
        existingStatus,
        exists: existingSnap.exists(),
      });
    }

    // ✅ KEEP: log when legacy path actually runs
    console.log('[utils/firestore] Saving ROOT DRAFT (legacy path):', {
      formId,
      saveMode,
      existingStatus,
    });

    // Ensure form details (metadata) are properly saved as part of the draft
    await setDoc(formRef, {
      compressedData: compressedDraftData, // Store compressed draft data in a single field
      layoutConfig: compressedLayoutConfig, // Store compressed layoutConfig separately
      theme: compressedThemeData, // ✅ Store compressed theme data separately
      fieldsMetadata: compressedFieldsMetadata,
      dropdownOptions: compressedDropdownOptions, 
      dropdownDependencies: compressedDropdownDependencies,
      submissionSettings: compressedSubmissionSettings,
//      submitStatus: compressedSubmitStatus, 
      status: 'draft', // Mark the form as a draft
      savedBy: userId, // Track who saved the draft
      savedAt: serverTimestamp(), // Track when the draft was saved
      lastModified: serverTimestamp(),
      lastModifiedBy: userId, // Track who last modified the draft
      organizationId: organizationId || '', // Ensure the organizationId is included correctly
      title: formDetails?.title || '', // Ensure title is saved separately
      description: formDetails?.description || '', // Ensure description is saved separately
      createdBy: formDetails?.createdBy || userId,
      owner: formDetails?.owner || userId,
      isTemplate: formDetails?.isTemplate || false,
      isPublic: formDetails?.isPublic || false,
      orgAccess             : formDetails?.orgAccess || 'none',
      sharedWith            : formDetails?.sharedWith|| {},

      // ─────────────────────────────────────────────────────────────
      // Draft marker: if this form is being configured as the org’s
      //    Asset Upload Form, set the flag now (explicit, discoverable).
      //    This does not change publish visibility; publish still enforces
      //    isPublic:false and orgAccess:'view'.
      // ─────────────────────────────────────────────────────────────
      ...(draftIsAssetUploadForm ? { isAssetUploadForm: true } : {}),

      // ─────────────────────────────────────────────────────────────
      // Form-scoped ingest mapping (draft): which UFIs represent
      //    identity/routing (canonical name, type, status, tag UFIs).
      //    Including it in drafts lets admins test ingestion before publish.
      //    Only attach when provided; keeps schema additive/flexible.
      // ─────────────────────────────────────────────────────────────
      ...(assetIngestConfig && typeof assetIngestConfig === 'object'
        ? {
            assetIngestConfig: {
              canonicalNameUfi : assetIngestConfig.canonicalNameUfi ?? null,
              assetTypeUfi     : assetIngestConfig.assetTypeUfi     ?? null,
              statusUfi        : assetIngestConfig.statusUfi        ?? null,
              tagUfis          : Array.isArray(assetIngestConfig.tagUfis)
                                 ? assetIngestConfig.tagUfis : [],
              collectionUfi    : assetIngestConfig.collectionUfi    ?? null,
            }
          }
        : {}),

      // Optional fields based on provided data
      submitButton: compressedDraftData.submitButton || null, // Retain submit button configuration if applicable
      fieldChanges: compressedDraftData.fieldChanges || null, // Field changes if provided
//      submissionSettings: compressedDraftData.submissionSettings || null, // Submission settings if provided
    }, { merge: true }); // Merge updates with the existing document


//    console.log(`[utils/firestore] Draft form with ID ${formId} saved successfully.`);
    return { status: 'success', message: 'Draft saved successfully.' };

  } catch (error) {
    console.error(`[utils/firestore] Error saving draft form with ID ${formId}:`, error);
    return { status: 'error', message: `Failed to save draft form: ${error.message}`, error };
  }
};

// ─────────────────────────────────────────────────────────────
// Draft overlay actions
// ─────────────────────────────────────────────────────────────

/**
 * Discard the enterprise “working copy” overlay while leaving the published form intact.
 *
 * This clears:
 *   - forms/{formId}.draftOverlay
 *   - forms/{formId}.draftOverlayStatus
 *   - forms/{formId}.draftOverlaySavedAt
 *   - forms/{formId}.draftOverlaySavedBy
 *
 * It does NOT modify:
 *   - root published blobs (layoutConfig/theme/etc.)
 *   - root status ('published')
 *
 * @param {Object} args
 * @param {string} args.formId
 * @param {string} args.userId
 * @returns {{status:'success'|'error', message:string}}
 */
export const discardDraftOverlayForForm = async ({ formId, userId }) => {
  console.log('[utils/firestore] discardDraftOverlayForForm called:', { formId, userId });

  if (!formId) {
    console.error('[utils/firestore] discardDraftOverlayForForm missing formId.');
    return { status: 'error', message: 'Missing formId.' };
  }

  try {
    const formRef = doc(db, 'forms', formId);
    const snap = await getDoc(formRef);

    if (!snap.exists()) {
      console.warn('[utils/firestore] discardDraftOverlayForForm: form doc not found.', { formId });
      return { status: 'error', message: 'Form not found.' };
    }

    const data = snap.data() || {};
    const overlayActive =
      (data?.draftOverlay?.status === 'draftOverlay') ||
      (data?.draftOverlayStatus === 'draftOverlay');

    console.log('[utils/firestore] discardDraftOverlayForForm overlay snapshot:', {
      formId,
      rootStatus: data?.status || 'none',
      overlayActive,
      hasDraftOverlayObject: !!data?.draftOverlay,
      draftOverlayStatus: data?.draftOverlayStatus || null,
      overlayStatus: data?.draftOverlay?.status || null,
    });

    if (!overlayActive && !data?.draftOverlay) {
      console.warn('[utils/firestore] discardDraftOverlayForForm: no overlay present; nothing to discard.', { formId });
      return { status: 'success', message: 'No draft overlay to discard.' };
    }

    await updateDoc(formRef, {
      // remove overlay payload
      draftOverlay: deleteField(),

      // remove query-friendly index fields
      draftOverlayStatus: deleteField(),
      draftOverlaySavedAt: deleteField(),
      draftOverlaySavedBy: deleteField(),

      // audit
      lastModified: serverTimestamp(),
      ...(userId ? { lastModifiedBy: userId } : {}),
    });

    console.log('[utils/firestore] discardDraftOverlayForForm: overlay discarded successfully.', { formId });
    return { status: 'success', message: 'Draft overlay discarded successfully.' };
  } catch (error) {
    console.error('[utils/firestore] discardDraftOverlayForForm FAILED:', error);
    return { status: 'error', message: `Failed to discard draft overlay: ${error.message}`, error };
  }
};



// Function to retrieve and decompress a form draft from Firestore
export const getDraftFormFromFirestore = async (formId) => {
//  console.log(`[getDraftFormFromFirestore] Fetching draft form with ID: ${formId}...`);

  try {
    // Reference the form document in Firestore
    const formRef = doc(db, 'forms', formId);
    const formDoc = await getDoc(formRef);

    if (formDoc.exists()) {
      const data = formDoc.data();

      // Check if the form contains compressed data
      if (data.compressedData) {
//        console.log('[getDraftFormFromFirestore] Compressed draft data found, decompressing...');

        // Decompress the draft data
        const decompressedData = decompressDraftData(data.compressedData);
//        console.log('[getDraftFormFromFirestore] Decompressed Draft Data:', decompressedData);

        return decompressedData;
      } else {
//        console.log('[getDraftFormFromFirestore] No compressed draft data found, returning raw form data...');
        return data; // Return raw data if no compressedData field exists
      }
    } else {
      console.error(`[getDraftFormFromFirestore] No form found with ID: ${formId}`);
      return null;
    }
  } catch (error) {
    console.error(`[getDraftFormFromFirestore] Error retrieving form ${formId}:`, error);
    return null;
  }
};

// Function to decompress draft data (this was previously in saveDraftFormToFirestore.js)
export const decompressDraftData = (compressedData) => {
  try {
//    console.log('[decompressDraftData] Decompressing draft form data...');

    // Decode Base64 data back to binary string
    const binaryString = atob(compressedData);
    const compressed = Uint8Array.from(binaryString, char => char.charCodeAt(0));

    // Decompress the binary data back into a string
    const decompressed = pako.inflate(compressed, { to: 'string' });

//    console.log('[decompressDraftData] Draft data decompressed successfully.');

    // Return parsed JSON
    return JSON.parse(decompressed);
  } catch (error) {
    console.error('[decompressDraftData] Error decompressing draft data:', error);
    return null;
  }
};


/**
 * Checks whether a form exists in Firestore and retrieves its current status.
 *
 * @param {string} formId - The ID of the form to check.
 * @returns {Object} - { exists: boolean, status: 'draft' | 'published' | 'none', data: formData }
 */
export const checkFormExistence = async (formId) => {
  if (!formId) {
    console.error('[checkFormExistence] ❌ ERROR: Missing formId.');
    return { exists: false, status: 'none', data: null };
  }

  console.log(`[checkFormExistence] 🔄 Checking existence of form with ID: ${formId}...`);

  try {
    const formRef = doc(db, 'forms', formId);
    const formDoc = await getDoc(formRef);

    if (formDoc.exists()) {
      const formData = formDoc.data();
      let status = formData.status || 'none'; // Default to 'none' if no status field exists

      // 🔥 Ensure status is either 'draft' or 'published' (avoid unexpected values)
      if (status !== 'draft' && status !== 'published') {
        console.warn(`[checkFormExistence] ⚠️ Unexpected status found: ${status}. Defaulting to 'none'.`);
        status = 'none';
      }

      console.log(`[checkFormExistence] ✅ Form found with status: ${status}`);
      return { exists: true, status, data: formData };
    } else {
      console.warn(`[checkFormExistence] ❌ No form found with ID: ${formId}`);
      return { exists: false, status: 'none', data: null };
    }
  } catch (error) {
    console.error(`[checkFormExistence] ❌ Error checking form existence:`, error);
    return { exists: false, status: 'none', data: null };
  }
};

/**
 * Saves or updates a published form in Firestore.
 *
 * Handles:
 * • Overwriting an existing published form
 * • Publishing an existing draft
 * • Creating a brand-new published form
 *
 * @param {Object}  formData        – The prepared (and now possibly merged) data.
 * @param {string}  existingStatus  – 'draft' | 'published' | 'none'
 * @param {boolean} [forceMerge]    – When true we _always_ use {merge:true}
 *                                    (FormPublishContext passes this as `true`.)
 *
 * @returns {{ status:'success'|'error', message:string }}
 */
export const savePublishedFormToFirestore = async (
  formData,
  existingStatus,
  forceMerge = false          //  3-arg signature
) => {
  /* =========================================================================
     0)  Destructure input
     ========================================================================= */
  const {
    formId,
    /* --------  compressed blobs  -------- */
    compressedDraftData,
    compressedLayoutConfig,
    compressedThemeData,
    compressedFieldsMetadata,
    compressedDropdownOptions,
    compressedDropdownDependencies,
    compressedSubmissionSettings,

    /* --------  meta from prepare-step ---- */
    userId,
    organizationId,
    formDetails  = {},   // may already include sharing flags

    /* --------  🆕 root-level flags  ------- */
    isTemplate      : root_isTemplate,
    templateScope   : root_templateScope,
    templateCategory: root_templateCategory,
    isPublic        : root_isPublic,
    orgAccess       : root_orgAccess,
    sharedWith      : root_sharedWith,
    /* root-level screenshot paths (pure Storage paths) */
    screenshotPath      : root_screenshotPath,
    screenshotThumbnail : root_screenshotThumbnail,
    /* --------  🆕 ingest mapping (form-scoped) ------- */
    assetIngestConfig: root_assetIngestConfig, // optional; admin-defined UFI mapping
  } = formData;

  console.log(
    `[utils/firestore] Attempting to publish form with ID: ${formId} for user: ${userId}…`
  );
  console.log(
    `[utils/firestore] Current form status in Firestore: ${existingStatus}`
  );

  try {
    /* ---------------------------------------------------------------------- */
    /* 1)  Prep refs & read existing doc (needed for backup / merge)          */
    /* ---------------------------------------------------------------------- */
    const formRef     = doc(db, 'forms', formId);
    const existingDoc = await getDoc(formRef);
    const existing    = existingDoc.exists() ? existingDoc.data() : {};

    // ─────────────────────────────────────────────────────────────
    // DEBUG: publish preflight snapshot (sizes + overlay state)
    // Helps detect "empty write" or "overlay still active" issues.
    // ─────────────────────────────────────────────────────────────
    try {
      const snap = {
        formId,
        existingStatus,
        rootStatus: existing?.status || 'none',
        hasOverlayObject: !!existing?.draftOverlay,
        draftOverlayStatus: existing?.draftOverlayStatus || null,
        overlayStatus: existing?.draftOverlay?.status || null,
        lens: {
          compressedData: (compressedDraftData || '').length,
          layoutConfig: (compressedLayoutConfig || '').length,
          theme: (compressedThemeData || '').length,
          fieldsMetadata: (compressedFieldsMetadata || '').length,
          dropdownOptions: (compressedDropdownOptions || '').length,
          dropdownDependencies: (compressedDropdownDependencies || '').length,
          submissionSettings: (compressedSubmissionSettings || '').length,
        },
      };
      console.log('[utils/firestore] PUBLISH PREFLIGHT snapshot:', snap);
    } catch (e) {
      console.warn('[utils/firestore] PUBLISH PREFLIGHT snapshot failed (non-fatal):', e?.message || e);
    }

    /* ---------------------------------------------------------------------- */
    /* 2)  Backup if we are overwriting an existing publication               */
    /* ---------------------------------------------------------------------- */
    if (existingStatus === 'published') {
      console.log('[utils/firestore] Overwriting existing published form. Creating backup…');
      const backupRef = doc(db, 'form_backups', `${formId}_${Date.now()}`);
      await setDoc(backupRef, existing);
      console.log('[utils/firestore] Backup created.');
    }

    /* ---------------------------------------------------------------------- */
    /* 3)  Build payload — merge with any existing sharing flags              */
    /*     (root-level template flags are taken from either prepare-step OR   */
    /*      the root_* variables we just pulled off formData)                */
    /* ---------------------------------------------------------------------- */
   const newFirestoreData = {
      /* ========== compressed blobs ========== */
      compressedData       : compressedDraftData,
      layoutConfig         : compressedLayoutConfig,
      theme                : compressedThemeData,
      fieldsMetadata       : compressedFieldsMetadata,
      dropdownOptions      : compressedDropdownOptions,
      dropdownDependencies : compressedDropdownDependencies,
      submissionSettings   : compressedSubmissionSettings,

      // ─────────────────────────────────────────────────────────────
      // ✅ Clear any enterprise “working copy” overlay AFTER publish.
      // Once we commit published blobs, the overlay must not remain,
      // otherwise the builder will keep resolving draftOverlay.*
      // and the navbar will keep showing "Last saved (draft)".
      // ─────────────────────────────────────────────────────────────
      draftOverlay          : deleteField(),
      draftOverlayStatus    : deleteField(),
      draftOverlaySavedAt   : deleteField(),
      draftOverlaySavedBy   : deleteField(),

      // ─────────────────────────────────────────────────────────────
      // Screenshot lifecycle hygiene on publish:
      // Clear stale failure markers so UI doesn’t treat old failures as current.
      // IMPORTANT: do NOT delete screenshotThumbnail/screenshotPath here —
      // keep showing the previous screenshot until the new one is ready.
      // ─────────────────────────────────────────────────────────────
      screenshotError       : deleteField(),
      screenshotStatus      : deleteField(),
      screenshotJobKey      : deleteField(),
      screenshotAssetId     : deleteField(),

      /* ========== core metadata ============ */
      status           : 'published',
      publishedBy      : userId,
      publishedAt      : serverTimestamp(),
      lastModified     : serverTimestamp(),
      lastModifiedBy   : userId,

      organizationId   : organizationId || '',
      title            : formDetails.title       ?? existing.title       ?? '',
      description      : formDetails.description ?? existing.description ?? '',
      createdBy        : formDetails.createdBy   ?? existing.createdBy   ?? userId,
      owner            : formDetails.owner       ?? existing.owner       ?? userId,

      /* ----------  🆕 template flags  ---------- */
      isTemplate       : root_isTemplate      ?? formDetails.isTemplate      ?? existing.isTemplate      ?? false,
      templateScope    : root_templateScope   ?? formDetails.templateScope   ?? existing.templateScope   ?? null,
      templateCategory : root_templateCategory?? formDetails.templateCategory?? existing.templateCategory?? null,

      /* ----------  sharing flags  ------------- */
      isPublic   : root_isPublic  ?? formDetails.isPublic  ?? existing.isPublic  ?? false,
      orgAccess  : root_orgAccess ?? formDetails.orgAccess ?? existing.orgAccess ?? 'none',
      sharedWith : root_sharedWith?? formDetails.sharedWith?? existing.sharedWith?? {},

      /* ----------  misc / ui ------------------ */
      previewToken        : formDetails.previewToken      ?? existing.previewToken      ?? null,
      /* store non-expiring Storage paths instead of signed URLs */
      screenshotPath      : root_screenshotPath      ?? formDetails.screenshotPath
                           ?? existing.screenshotPath ?? null,
      screenshotThumbnail : root_screenshotThumbnail ?? formDetails.screenshotThumbnail
                           ?? existing.screenshotThumbnail ?? null,

      /* (optional) keep the legacy URL for backwards compat – uncomment if required */
      // screenshotUrl   : formDetails.screenshotUrl  ?? existing.screenshotUrl  ?? null,
    };

   // ─────────────────────────────────────────────────────────────
   // Form-scoped ingest mapping: which UFIs represent identity/
   //    routing (canonical name, type, status, tag fields, etc.)
   //    Only attach when provided; keeps schema additive/flexible.
   // ─────────────────────────────────────────────────────────────
   if (root_assetIngestConfig && typeof root_assetIngestConfig === 'object') {
     newFirestoreData.assetIngestConfig = {
       canonicalNameUfi : root_assetIngestConfig.canonicalNameUfi ?? null,
       assetTypeUfi     : root_assetIngestConfig.assetTypeUfi     ?? null,
       statusUfi        : root_assetIngestConfig.statusUfi        ?? null,
       tagUfis          : Array.isArray(root_assetIngestConfig.tagUfis)
                          ? root_assetIngestConfig.tagUfis : [],
       collectionUfi    : root_assetIngestConfig.collectionUfi    ?? null,
     };
   }

    // ─────────────────────────────────────────────────────────────
    // DAM-lite: ensure the **Asset Upload Form** is org-wide viewable
    // (NOT public) when published, and carry the marker through.
    // This does NOT affect any other form types.
    // ─────────────────────────────────────────────────────────────
    const isAssetUpload =
      (formDetails?.isAssetUploadForm === true) ||
      (existing?.isAssetUploadForm === true);

    if (isAssetUpload) {
      newFirestoreData.isAssetUploadForm = true; // keep the marker
      newFirestoreData.isPublic         = false; // never public
      newFirestoreData.orgAccess        = 'view';// org-wide readable
      // NOTE: Do not widen 'sharedWith' here; orgAccess handles normal org users.
    }

    console.log('[utils/firestore] Saving form to Firestore (merge=', forceMerge, ')…');

    // DEBUG: This confirms publish is writing root blobs (NOT overlay)
    console.warn('[utils/firestore] PUBLISH COMMIT: writing root compressedData/layoutConfig/theme/etc.', {
      formId,
      existingStatus,
      forceMerge,
      stack: new Error().stack,
    });

    /* ---------------------------------------------------------------------- */
    /* 4)  Commit — always merge unless caller really wants an overwrite      */
    /* ---------------------------------------------------------------------- */
   await setDoc(formRef, newFirestoreData, { merge: (forceMerge ?? true) });

    // ─────────────────────────────────────────────────────────────
    // DEBUG: read-back after publish commit
    // Confirms overlay cleared + root blobs present.
    // ─────────────────────────────────────────────────────────────
    try {
      const verifySnap = await getDoc(formRef);
      const v = verifySnap.exists() ? (verifySnap.data() || {}) : {};

      console.log('[utils/firestore] PUBLISH READBACK snapshot:', {
        formId,
        rootStatus: v?.status || 'none',
        hasOverlayObject: !!v?.draftOverlay,
        draftOverlayStatus: v?.draftOverlayStatus || null,
        overlayStatus: v?.draftOverlay?.status || null,
        lens: {
          compressedData: (v?.compressedData || '').length,
          layoutConfig: (v?.layoutConfig || '').length,
          theme: (v?.theme || '').length,
          fieldsMetadata: (v?.fieldsMetadata || '').length,
          dropdownOptions: (v?.dropdownOptions || '').length,
          dropdownDependencies: (v?.dropdownDependencies || '').length,
          submissionSettings: (v?.submissionSettings || '').length,
        },
      });
    } catch (e) {
      console.warn('[utils/firestore] PUBLISH READBACK failed (non-fatal):', e?.message || e);
    }

    console.log('[utils/firestore] Form published successfully.');
    return { status: 'success', message: 'Form published successfully.' };
  } catch (error) {
    console.error('[utils/firestore] ❌ Error publishing form:', error);
    return {
      status : 'error',
      message: `Failed to publish form: ${error.message}`,
      error,
    };
  }
};



// Function to save the naming structure
export const saveNamingStructure = async (structure) => {
  const namingStructureDocRef = doc(db, 'namingStructures', 'current');
  try {
    await setDoc(namingStructureDocRef, { structure });
    console.log("Successfully saved the naming structure");
    return { status: 'success', message: 'Naming structure successfully saved.' };
  } catch (error) {
    console.error("Error saving the naming structure:", error);
    return { status: 'error', message: 'Error saving the naming structure.', error };
  }
};


// Function to retrieve the naming structure
export const getNamingStructure = async () => {
  const namingStructureDocRef = doc(db, 'namingStructures', 'current');
  try {
    const docSnapshot = await getDoc(namingStructureDocRef);
    if (docSnapshot.exists()) {
      console.log("Naming structure found");
      return { status: 'success', message: 'Naming structure fetched successfully.', structure: docSnapshot.data().structure };
    } else {
      console.log("No naming structure found");
      return { status: 'warning', message: 'No naming structure found.', structure: '' };
    }
  } catch (error) {
    console.error("Error fetching the naming structure:", error);
    return { status: 'error', message: 'Error fetching the naming structure.', error };
  }
};




/**
 * Fetches all forms for a user, including:
 * - Forms created by the user
 * - Forms shared with the user
 * - Forms visible org-wide   (orgAccess ≠ 'none')    ★ NEW
 * - Public, published forms (isPublic == true)       ★ NEW
 * - Supports pagination
 *
 *  (The old “bucket 3” that surfaced *every* form in the organisation
 *   has been **removed** to stop restricted forms leaking.)
 *
 * @param {string}  userId         - The ID of the current user.
 * @param {string}  orgId          - The ID of the organization.
 * @param {Object}  lastDoc        - The last doc from previous fetch (pagination).
 * @param {number}  pageSize       - # of forms per page.
 * @param {boolean} returnIdsOnly  - Whether to return only IDs.
 * @returns {Object} { status, forms, lastVisibleDoc, totalPages }
 */
export const getFormsForUser = async (
  userId,
  orgId,
  lastDoc       = null,
  pageSize      = 10,
  returnIdsOnly = false
) => {
  if (!userId) {
    return { status: 'error', message: 'No user ID provided.', forms: [] };
  }

  try {
    //    console.log(`[getFormsForUser] 🔄 Fetching forms for user: ${userId}, org: ${orgId}, pageSize: ${pageSize}`);

    // Firestore Queries: Separate buckets
    const baseQuery = collection(db, 'forms');

    // 1️⃣  My own forms
    let userFormsQuery = query(
      baseQuery,
      where('createdBy', '==', userId),
      where('status', 'in', ['draft', 'published']),
      orderBy('lastModified', 'desc'),
      limit(pageSize)
    );

    /* 2️⃣  Explicitly shared with me */
    let sharedFormsQuery = query(
      baseQuery,
      where('sharedWithIds', 'array-contains', userId),
      where('status', 'in', ['draft', 'published']),
      orderBy('lastModified', 'desc'),
      limit(pageSize)
    );


    // 4️⃣  Org-wide **shared** (orgAccess ≠ 'none')  ★ NEW
    let orgWideFormsQuery = query(
      baseQuery,
      where('organizationId', '==', orgId),
      where('orgAccess', 'in', ['view', 'edit']),
      where('status', 'in', ['draft', 'published']),
      orderBy('lastModified', 'desc'),
      limit(pageSize)
    );


    /* ---------------------------------------------------- *
     * 🗂  Pagination
     * ---------------------------------------------------- */
    if (lastDoc) {
      //    console.log(`[getFormsForUser] ⏩ Paginating after lastDoc:`, lastDoc);
      userFormsQuery    = query(userFormsQuery,    startAfter(lastDoc));
      sharedFormsQuery  = query(sharedFormsQuery,  startAfter(lastDoc));
      // orgFormsQuery     = query(orgFormsQuery,     startAfter(lastDoc));  // 🔒 removed
      orgWideFormsQuery = query(orgWideFormsQuery, startAfter(lastDoc));
      //publicFormsQuery  = query(publicFormsQuery,  startAfter(lastDoc));
    }

    /* ---------------------------------------------------- *
     * 🔍 Fetch snapshots in parallel
     * ---------------------------------------------------- */
    const [
      userFormsSnapshot,
      sharedFormsSnapshot,
      // orgFormsSnapshot,                                // 🔒 removed
      orgWideFormsSnapshot,
      //publicFormsSnapshot
    ] = await Promise.all([
      getDocs(userFormsQuery),
      getDocs(sharedFormsQuery),
      // getDocs(orgFormsQuery),                          // 🔒 removed
      getDocs(orgWideFormsQuery),
      //getDocs(publicFormsQuery)
    ]);

    /* ---------------------------------------------------- *
     * 🛠  Normalise docs
     * ---------------------------------------------------- */
    const extractForms = (snapshot) =>
      snapshot.docs.map((doc) => {
        const formData = {
          id          : doc.id,
          ...doc.data(),
          title       : doc.data().title || doc.data().formDetails?.title || 'Untitled Form',
          description : doc.data().description || doc.data().formDetails?.description || 'No description',
        };
        //    console.log(`[DEBUG] 📝 Extracted Form Data:`, formData);
        return formData;
      });

    //  AUF guard — identify Asset Upload Forms so they never hit the Home grid
    //    We support multiple markers (current and future-proof):
    //    • isAssetUploadForm === true (authoritative flag set on publish)
    //    • assetIngestConfig present (AUF-specific mapping)
    //    • formType === 'assetUpload' (if you ever add a normalized type)
    const isAUF = (f) =>
      f?.isAssetUploadForm === true ||
      !!f?.assetIngestConfig ||
      f?.formType === 'assetUpload';

    // Apply per-bucket filtering to keep pagination math and dedupe stable
    const userForms    = extractForms(userFormsSnapshot).filter(f => !isAUF(f));
    const sharedForms  = extractForms(sharedFormsSnapshot).filter(f => !isAUF(f));
    // const orgForms     = extractForms(orgFormsSnapshot);                // 🔒 removed
    const orgWideForms = extractForms(orgWideFormsSnapshot).filter(f => !isAUF(f));
    //const publicForms  = extractForms(publicFormsSnapshot);


    //    console.log('[getFormsForUser] 📄 User Forms   :', userForms.map(f   => f.id));
    //    console.log('[getFormsForUser] 📄 Shared Forms :', sharedForms.map(f => f.id));
    //    console.log('[getFormsForUser] 📄 Org Forms    :', orgForms.map(f   => f.id));  // 🔒 removed
    //    console.log('[getFormsForUser] 📄 Org-Wide     :', orgWideForms.map(f=> f.id));
    //    console.log('[getFormsForUser] 📄 Public       :', publicForms.map(f => f.id));

    /* ---------------------------------------------------- *
     * 🔗 Merge & deduplicate  (first occurrence wins)
     * ---------------------------------------------------- */
    const allForms = [
      ...userForms,
      ...sharedForms,
      ...orgWideForms,   // orgAccess ≠ 'none'
    ];

    const uniqueForms = Array.from(
      new Map(allForms.map((form) => [form.id, form])).values()
    );

    // (Optional) Final guard in case any AUF sneaks in from legacy data:
    // const uniqueForms = Array.from(
    //   new Map(allForms.map((form) => [form.id, form])).values()
    // ).filter(f => !isAUF(f));

    //    console.log(`[getFormsForUser] 🛠 Deduplicated Forms:`, uniqueForms);

    /* ---------------------------------------------------- *
     *   Fast “IDs only” mode
     * ---------------------------------------------------- */
    if (returnIdsOnly) {
      const uniqueIds = uniqueForms.map((form) => form.id);
      return { status: 'success', formIds: uniqueIds };
    }

    /* ---------------------------------------------------- *
     * 📄 Pagination maths
     * ---------------------------------------------------- */
    const totalPages   = Math.max(1, Math.ceil(uniqueForms.length / pageSize));
    const lastVisibleDoc =
         userFormsSnapshot.docs[userFormsSnapshot.docs.length - 1]
      || sharedFormsSnapshot.docs[sharedFormsSnapshot.docs.length - 1]
      || orgWideFormsSnapshot.docs[orgWideFormsSnapshot.docs.length - 1]
      //|| publicFormsSnapshot.docs[publicFormsSnapshot.docs.length - 1]
      // || orgFormsSnapshot.docs[orgFormsSnapshot.docs.length - 1]       // removed
      || null;

    return {
      status: 'success',
      forms : uniqueForms,
      lastVisibleDoc,
      totalPages,
    };
  } catch (error) {
    console.error('[getFormsForUser] ❌ Error fetching forms:', error);
    return { status: 'error', message: 'Error fetching forms.', error };
  }
};



/**
 * setFormPublicStatus updates the "isPublic" field of the specified form,
 * optionally logging or returning status objects. It also sets lastModified.
 *
 * @param {string} formId - The Firestore doc ID for the form.
 * @param {boolean} isPublicValue - Whether the form should be public or not.
 * @returns {Object} - { status: 'success' | 'error', message: string, error?: any }
 */
export const setFormPublicStatus = async (formId, isPublicValue) => {
  if (!formId) {
    console.error("[utils/firestore] setFormPublicStatus called without a formId!");
    return {
      status: 'error',
      message: 'No form ID provided.'
    };
  }

//  console.log(`[utils/firestore] setFormPublicStatus called with formId=${formId}, isPublicValue=${isPublicValue}`);

  try {
    // Reference the form doc in Firestore
    const formRef = doc(db, 'forms', formId);

    // Perform a minimal update for isPublic
    await updateDoc(formRef, {
      isPublic: isPublicValue,
      lastModified: serverTimestamp()
    });

//    console.log(`[utils/firestore] Successfully updated isPublic to ${isPublicValue} for form ${formId}.`);
    return {
      status: 'success',
      message: `Successfully updated isPublic to ${isPublicValue}.`
    };
  } catch (error) {
    console.error("[utils/firestore] Error updating isPublic in setFormPublicStatus:", error);
    return {
      status: 'error',
      message: error.message,
      error
    };
  }
};



// Asynchronous function to handle non-critical submit actions
export const handleActionAsync = async (action, submissionData, userData, orgId, formId) => {  
  switch (action.type) {
    case 'sendEmail':
      console.log(`Preparing to send email using template: ${action.emailTemplate}`);
      // Simulate email sending delay
      setTimeout(() => {
        console.log(`Email sent using template: ${action.emailTemplate} to recipient: ${submissionData[action.recipientField]}`);
      }, 1000);
      break;
    case 'saveProgress':
      console.log(`Simulating asynchronous progress saving for form ID: ${formId}`);
      setTimeout(() => {
        console.log(`Progress saved for form ID: ${formId}`);
      }, 1000);
      break;
    default:
      console.log(`Action ${action.type} does not support asynchronous handling.`);
  }
};

export const processSubmitActions = async (formId, submissionData, userData, handleAction) => {
  console.log(`[processSubmitActions] Processing submit actions for form ID: ${formId}`);

  const formDetails = await getFormById(formId); // Fetch form status
  if (formDetails.status === 'error' || !formDetails.form) {
    console.error("[processSubmitActions] Form not found or invalid.");
    return { status: 'error', message: 'Invalid form.' };
  }

  const formStatus = formDetails.form.status; // Extract draft or published status

  if (formStatus === 'draft') {
    console.warn(`[processSubmitActions] Skipping submission actions for draft form: ${formId}`);
    return { status: 'skipped', message: 'Draft forms do not process submit actions.' };
  }

  // Fetch submit button config
  const { status, config } = await getSubmitButtonConfig(formId);
  if (status !== 'success') {
    console.error("[processSubmitActions] No configuration available to process submit actions.");
    return { status: 'error', message: 'Submit button configuration is required to process actions.' };
  }

  try {
    const asyncActions = [];
    for (const action of config.actions) {
      if (['sendEmail', 'saveProgress'].includes(action.type)) {
        asyncActions.push(handleActionAsync(action, submissionData, userData, formId));
      } else {
        await handleAction(action, submissionData, userData);
      }
    }

    await Promise.all(asyncActions);
    console.log("[processSubmitActions] All actions processed successfully.");
    return { status: 'success', message: 'All submit actions processed successfully.' };
  } catch (error) {
    console.error("[processSubmitActions] Error processing submit actions:", error);
    return { status: 'error', message: 'Failed to process submit actions.', error };
  }
};


// Function to fetch the submit button configuration for a specific form
export const getSubmitButtonConfig = async (formId) => {
//  console.log(`[getSubmitButtonConfig] Fetching submit button configuration for form ID: ${formId}`);
  const formRef = doc(db, "forms", formId);
  try {
    const formSnap = await getDoc(formRef);
    if (formSnap.exists()) {
      // Either uses the submitButton from Firestore or fall back to defaults
      let submitButton = formSnap.data().submitButton || getDefaultSubmitButtonConfig();
//      console.log("[getSubmitButtonConfig] Submit button configuration fetched successfully.");

      // ✅ Ensure displayInCard.customMessage is a string (HTML), not a Delta array
      if (submitButton.actions) {
        submitButton.actions = submitButton.actions.map((action) => {
          if (action.type === "displayInCard") {
            // If customMessage is not a string (e.g., an array), convert or fallback
            if (typeof action.customMessage !== "string") {
              console.warn(
                "[getSubmitButtonConfig] Found a non-string customMessage (likely Delta). Converting to empty HTML or fallback."
              );
              // Minimal fallback: empty HTML or you could do some array->HTML conversion
              action.customMessage = "<p></p>";
            }
          }
          return action;
        });
      }

      return { status: "success", config: submitButton };
    } else {
      console.warn("[getSubmitButtonConfig] No form found, using default submit button configuration.");
      return { status: "success", config: getDefaultSubmitButtonConfig() };
    }
  } catch (error) {
    console.error("[getSubmitButtonConfig] Error fetching submit button configuration:", error);
    return { status: "error", message: "Failed to fetch submit button configuration.", error };
  }
};

// Provide a default submit button configuration
export const getDefaultSubmitButtonConfig = () => ({
  label: "Submit",
  actions: [
    {
      type: "redirect",
      targetUrl: "", // Default or placeholder URL, can be set later in the UI
    },
    {
      type: "sendEmail",
      emailTemplate: "submissionConfirmation", // Default template, can be customized
      recipientField: "email", // Assuming 'email' is a field in your form
    },
    {
      type: "displayToast",
      message: "Your form has been successfully submitted!", // Default toast message
    },
    {
      type: "displayInCard",
      cardTitle: "Submission Successful",
      cardDescription: "Thank you for your submission:",
      customMessage: "<p>Your custom message goes here.</p>",
      errorMessage: "Fill out all required fields and a submission will be generated for you here.",
    },
    {
      type: "addToSubmissionsTable",
      tableId: "submissionTable", // Default table ID, can be customized
    },
  ],
  style: {
    alignment: "center",
    backgroundColor: "#4CAF50",
    textColor: "#FFFFFF",
    fontSize: "16px",
    fontWeight: "bold",
    height: "50px",
    width: "150px",
    borderRadius: "5px",
  },
});


/**
 * Submits form data to Firestore with batch processing.
 * - Ensures proper structure for flattened submission data.
 * - Includes metadata like `submittedAt`, `submitterId`, `formId`.
 * - Uses Firestore batch write for optimized performance.
 *
 * @param {string} formId - The ID of the form being submitted.
 * @param {object} submissionData - The processed submission data.
 * @param {string} userId - The ID of the user submitting the form.
 * @returns {Promise<object>} - Status message and submission ID.
 */
export const submitFormData = async (formId, submissionData, userId) => {
  console.log(`[submitFormData] Submitting form data for form ID: ${formId}`);

  try {
    if (!formId || !submissionData || Object.keys(submissionData).length === 0) {
      throw new Error('[submitFormData] Invalid form ID or submission data.');
    }

    // 🔹 Prepare submission data with correct UFI mappings
    const preparedSubmissionData = Object.entries(submissionData).reduce((acc, [ufi, value]) => {
      if (ufi.startsWith('label_')) {
        acc[ufi] = value; // Store labels separately
      } else {
        acc[ufi] = typeof value === 'object' && value.optionId ? value : { value };
      }
      return acc;
    }, {});

    // 🔹 Ensure submissionActions is stored as a string for Firestore consistency
    if (preparedSubmissionData.submissionActions && typeof preparedSubmissionData.submissionActions !== 'string') {
      preparedSubmissionData.submissionActions = JSON.stringify(preparedSubmissionData.submissionActions);
    }

    // 🔹 Create a batch write for efficient Firestore storage
    const batch = writeBatch(db);
    const submissionsRef = collection(db, 'formSubmissions');
    const submissionDocRef = doc(submissionsRef); // Auto-generate a unique submission ID

    batch.set(submissionDocRef, {
      formId,
      organizationId: submissionData.organizationId || null,
      responses: preparedSubmissionData,
      submittedAt: serverTimestamp(), // 🔹 Ensure accurate submission timestamp
      submitterId: userId || 'anonymous', // 🔹 Store submitter's ID (default: anonymous)
    });

    // 🔹 Commit batch write to Firestore
    await batch.commit();
    console.log(`[submitFormData] Form data submitted successfully with ID: ${submissionDocRef.id}`);

    return { status: 'success', message: 'Form data submitted successfully.', docId: submissionDocRef.id };
  } catch (error) {
    console.error('[submitFormData] Error submitting form data:', error);
    return { status: 'error', message: 'Error submitting form data.', error };
  }
};

/**
 * Fetches form submissions from Firestore with pagination.
 * ──────────────────────────────────────────────────────────────────────────
 * • Uses Firestore queries to retrieve submissions in batches.
 * • Supports `startAfter` for pagination.
 * • Optional date-range filter (JS Date objects are okay).
 * • Optional per-viewer filter (`submitterId === viewerId`) so the
 *   caller can fetch “just my rows” in public forms.
 * • ★ NEW: Optional per-session filter (`submitterSessionId === sessionId`)
 *   which, when provided, takes precedence over submitterId to isolate the
 *   current browser/incognito session (used by Display Table).
 *
 * @param {string}         formId        – The ID of the form.
 * @param {object|null}    lastVisible   – The last fetched document for pagination.
 * @param {number}         pageSize      – Number of submissions to fetch per page.
 * @param {Date|null}      startDate     – Lower-bound (inclusive) or null.
 * @param {Date|null}      endDate       – Upper-bound (inclusive) or null.
 * @param {string}         sortField     – Field to sort on (default 'submittedAt').
 * @param {'asc'|'desc'}   sortDir       – Sort direction (default 'desc').
 * @param {string|null}    submitterId   – **NEW**. If supplied, results are
 *                                         filtered where submitterId == value.
 * @param {string|null}    submitterSessionId – **NEWER**. If supplied, results are
 *                                              filtered where submitterSessionId == value (preferred).
 *
 * @returns {Promise<object>}            – { status, submissions[], lastVisible }
 */
export const getFormSubmissions = async (
  formId,
  lastVisible  = null,
  pageSize     = 25,
  startDate    = null,
  endDate      = null,          // inclusive
  sortField    = 'submittedAt',
  sortDir      = 'desc',
  submitterId  = null,          // ★ NEW
  submitterSessionId = null     // ★ NEWER (preferred when present)
) => {
//  console.log(
//    `[getFormSubmissions] Fetching submissions for formId=${formId} ` +
//    `(pageSize=${pageSize}, submitterId=${submitterId ?? 'ALL'}, submitterSessionId=${submitterSessionId ?? 'NONE'})`
//  );

  try {
    if (!formId) {
      throw new Error('[getFormSubmissions] Invalid form ID.');
    }

    /* ─────────────────────────────────────────────────────────────
       Build Firestore query
       ─────────────────────────────────────────────────────────── */
    const submissionsRef = collection(db, 'formSubmissions');

    // ★ Prefer session-scoped isolation when provided; else fall back to submitterId; else no viewer filter
    const viewerClause = submitterSessionId
      ? [where('submitterSessionId', '==', submitterSessionId)]
      : (submitterId ? [where('submitterId', '==', submitterId)] : []);

    let q = query(
      submissionsRef,
      where('formId', '==', formId),

      // 🔸 per-viewer/per-session isolation (optional; session preferred)
      ...viewerClause,

      // Date-range window (optional)
      ...(startDate ? [where('submittedAt', '>=', startDate)] : []),
      ...(endDate   ? [where('submittedAt', '<=', (() => {
          const d = new Date(endDate);
          d.setHours(23, 59, 59, 999);     // make end-day inclusive
          return d;
        })())] : []),

      orderBy(sortField, sortDir),
      limit(pageSize)
    );

    // Pagination
    if (lastVisible) q = query(q, startAfter(lastVisible));

    /* ─────────────────────────────────────────────────────────────
       Execute query
       ─────────────────────────────────────────────────────────── */
    const querySnapshot = await getDocs(q);

    const submissions  = [];
    let   newLastVis   = null;

    querySnapshot.forEach((doc) => {
      const submissionData = doc.data();

      // ✦ Ensure nested responses retain the original structure
      const structuredResponses = Object.entries(submissionData.responses || {})
        .reduce((acc, [ufi, resp]) => {
          if (ufi.startsWith('label_')) {
            acc[ufi] = resp;                         // keep label mapping intact
          } else {
            acc[ufi] = resp.optionId
              ? { ...resp }                          // dropdown object preserved
              : { value: resp };                     // primitives normalised
          }
          return acc;
        }, {});

      // Parse submissionActions if it’s a JSON string
      if (
        structuredResponses.submissionActions &&
        typeof structuredResponses.submissionActions === 'string'
      ) {
        try {
          structuredResponses.submissionActions =
            JSON.parse(structuredResponses.submissionActions);
        } catch (err) {
          console.error(
            '[getFormSubmissions] Error parsing submissionActions JSON:',
            err
          );
        }
      }

      submissions.push({
        id: doc.id,
        ...submissionData,
        responses: structuredResponses,
      });
    });

    // Track pagination cursor
    if (!querySnapshot.empty) {
      newLastVis = querySnapshot.docs[querySnapshot.docs.length - 1];
    }

//    console.log(
//      `[getFormSubmissions] Fetched ${submissions.length} rows ` +
//      `(viewer filter = session:${submitterSessionId ?? '-'} user:${submitterId ?? '-'})`
//    );

    return {
      status     : 'success',
      submissions,
      lastVisible: newLastVis,
    };
  } catch (error) {
    console.error(
      `[getFormSubmissions] Error fetching submissions for formId ${formId}:`,
      error
    );
    return { status: 'error', message: 'Failed to fetch submissions.', error };
  }
};

// Function to fetch a form by its ID
export const getFormById = async (formId) => {
  try {
    const formRef = doc(db, 'forms', formId);
    const formSnapshot = await getDoc(formRef);

    if (formSnapshot.exists()) {
      // Convert the Firestore document into a usable object
//      console.log(`Form found with ID: ${formId}`);
      return { status: 'success', form: { id: formSnapshot.id, ...formSnapshot.data() } };
    } else {
      console.warn(`No form found with ID: ${formId}`);
      return { status: 'not_found', message: 'Form not found.' }; // Return structured response for better state management
    }
  } catch (error) {
    console.error(`Error fetching form with ID ${formId}:`, error);
    return { status: 'error', message: 'Failed to fetch form.', error: error }; // Return error details for UI handling and logging
  }
};


// Function to check if a user has permission to perform a specific action
export const checkUserPermission = async (userId, actionType) => {
  try {
    // Fetch the user's role from the accounts collection
    const userRef = doc(db, 'accounts', userId);
//    console.log(`[Utils] Fetching user with ID: ${userId}`); // Log the user ID being used for the fetch

    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      console.error('[Utils] User not found.');
      return false; // Return false if the user does not exist
    }

    const userRole = userSnap.data().role;
//    console.log(`[Utils] User role: ${userRole}`); // Log the role

    if (!userRole) {
      console.error('[Utils] User has no role assigned.');
      return false; // Return false if no role is assigned to the user
    }

    // Fetch the permissions for the user's role from the roles collection
    const roleRef = doc(db, 'roles', userRole);
    const roleSnap = await getDoc(roleRef);
    if (!roleSnap.exists()) {
      console.error(`[Utils] Role ${userRole} not found.`);
      return false; // Return false if the role does not exist
    }

    const permissions = roleSnap.data().permissions || [];
//    console.log(`[Utils] Permissions for role ${userRole}:`, permissions); // Log the permissions

    // Check if the permissions array includes the required action
    const hasPermission = permissions.includes(actionType);
//    console.log(`[Utils] Permission for action "${actionType}":`, hasPermission); // Log the result

    return hasPermission;
  } catch (error) {
    console.error('Error checking user permissions:', error);
    return false; // Return false on error
  }
};


// Function to delete a field from a form, supporting handling of different field types
export const deleteFieldFromForm = async (fieldUFI, formId) => {
  const batch = writeBatch(db);
  let retries = 3; // Number of retries for the deletion process

  const attemptDeletion = async (retryCount) => {
    try {
      const fieldRef = doc(db, 'fieldMetadata', fieldUFI);
      const fieldMetaSnap = await getDoc(fieldRef);
      if (!fieldMetaSnap.exists()) {
        throw new Error(`No field metadata found for UFI: ${fieldUFI}`);
      }
      const fieldType = fieldMetaSnap.data().type;
      batch.delete(fieldRef);

      const formLayoutRef = doc(db, 'forms', formId);
      const formLayoutSnap = await getDoc(formLayoutRef);
      if (formLayoutSnap.exists()) {
        let layoutConfig = formLayoutSnap.data().layoutConfig;
        if (layoutConfig && layoutConfig.rows) {
          layoutConfig.rows = layoutConfig.rows.map(row => ({
            ...row,
            fields: row.fields.filter(id => id !== fieldUFI)
          }));
          batch.update(formLayoutRef, { layoutConfig });
        }
      }

      // Handle specific field types, if additional actions are needed
      switch (fieldType) {
        case 'dropdown':
          const optionsRef = doc(db, 'dropdownOptions', fieldUFI);
          batch.delete(optionsRef);
          break;
        case 'text':
        case 'url':
        case 'date':
          // No specific actions required for these types
          break;
        // Extend with more cases as needed
      }

      // Update or delete dependencies if necessary
      const dependenciesRef = doc(db, 'dropdownDependencies', formId);
      const dependenciesSnap = await getDoc(dependenciesRef);
      if (dependenciesSnap.exists()) {
        let dependenciesData = dependenciesSnap.data();
        if (dependenciesData && dependenciesData.hasOwnProperty(fieldUFI)) {
          delete dependenciesData[fieldUFI];
          batch.update(dependenciesRef, dependenciesData);
        }
      }

      // Commit all changes as a batch
      await batch.commit();
      console.log(`Successfully deleted field with UFI: ${fieldUFI} from form ID: ${formId}`);
    } catch (error) {
      if (retryCount > 0) {
        console.log(`Retrying deletion, attempts remaining: ${retryCount}`);
        await attemptDeletion(retryCount - 1);
      } else {
        console.error(`Failed to delete field with UFI: ${fieldUFI} from form ID: ${formId}: ${error}`);
        throw new Error(`Failed to delete field with UFI: ${fieldUFI} from form ID: ${formId}: ${error}`);
      }
    }
  };

  return attemptDeletion(retries);
};

// Function to archive a form and all its related data (fields, options, submissions, dependencies)
export const archiveFormAndRelatedData = async (formId) => {
  const batch = writeBatch(db); // Firestore batch operation

  try {
    console.log(`[archiveFormAndRelatedData] Starting archiving process for formId: ${formId}`);

    // 1. Archive the form itself
    const formRef = doc(db, 'forms', formId);
    batch.update(formRef, {
      archived     : true,
      status       : 'archived',
      archivedAt   : serverTimestamp(),
      lastModified : serverTimestamp(),            // ← bump lastModified
      isPublic     : false,
      orgAccess    : 'none',
      sharedWith   : {},
      // sharedWithIds: [],                        // ← OPTIONAL if you maintain this field
    });

    /* ───────────── vanity URL cleanup ─────────────
       Make vanity cleanup best-effort so failures
       here don’t block the entire archive.        */
    try {
      const formSnap = await getDoc(formRef);
      const slug = formSnap.exists() ? (formSnap.data().vanitySlug || '').trim() : '';

    if (slug) {
      try {
        await clearFormVanitySlug(formId);

        batch.update(formRef, {
          vanitySlug: null,
          vanityUrl: null,
        });

        console.log(
          '[archiveFormAndRelatedData] Cleared vanity slug ' +
            `"${slug}" for form ${formId}.`
        );
      } catch (e) {
        console.warn(
          '[archiveFormAndRelatedData] Failed to clear vanity ' +
            'slug through callable:',
          e
        );
      }
    }
    } catch (e) {
      console.warn('[archiveFormAndRelatedData] Skipping vanity cleanup (could not read form):', e);
    }
    /* ─────────── end vanity cleanup ─────────── */

    // 2. Archive related field metadata
    const fieldMetadataRef = collection(db, 'fieldMetadata');
    const fieldQuery = query(fieldMetadataRef, where("formId", "==", formId));
    const fieldSnapshot = await getDocs(fieldQuery);
    fieldSnapshot.forEach((fieldDoc) => {
      batch.update(doc(db, 'fieldMetadata', fieldDoc.id), { archived: true });
    });

    // 3. Archive related dropdown options
    const dropdownOptionsRef = collection(db, 'dropdownOptions');
    const dropdownQuery = query(dropdownOptionsRef, where("formId", "==", formId));
    const dropdownSnapshot = await getDocs(dropdownQuery);
    dropdownSnapshot.forEach((dropdownDoc) => {
      batch.update(doc(db, 'dropdownOptions', dropdownDoc.id), { archived: true });
    });

    // 4. Archive related dropdown dependencies
    const dependenciesRef = collection(db, 'dropdownDependencies');
    const dependenciesQuery = query(dependenciesRef, where("formId", "==", formId));
    const dependenciesSnapshot = await getDocs(dependenciesQuery);
    dependenciesSnapshot.forEach((dependencyDoc) => {
      batch.update(doc(db, 'dropdownDependencies', dependencyDoc.id), { archived: true });
    });

    // 5. Archive related submissions
    const submissionsRef = collection(db, 'formSubmissions');
    const submissionsQuery = query(submissionsRef, where("formId", "==", formId));
    const submissionsSnapshot = await getDocs(submissionsQuery);
    submissionsSnapshot.forEach((submissionDoc) => {
      batch.update(doc(db, 'formSubmissions', submissionDoc.id), { archived: true });
    });

    // 6. Commit all archiving operations as a batch
    await batch.commit();
    console.log(`[archiveFormAndRelatedData] Successfully archived form and related data for formId: ${formId}`);

    /* ─────────────────────────────────────────────────────────────
       OPTIONAL CLEANUP: Clear org AUF pointer if it pointed to this form
       This keeps organizations/{orgId}.assetUploadFormId in sync
       immediately after archive (non-fatal if it fails).
       ───────────────────────────────────────────────────────────── */
    try {
      // Re-read the archived form to get orgId and confirm AUF marker
      const archivedSnap = await getDoc(doc(db, 'forms', formId));
      const d = archivedSnap.exists() ? archivedSnap.data() : null;

      const orgId = d?.organizationId || null;
      const wasAuf = d?.isAssetUploadForm === true;

      if (orgId && wasAuf) {
        const orgRef = doc(db, 'organizations', orgId);
        const orgSnap = await getDoc(orgRef);

        if (orgSnap.exists()) {
          const ptr = orgSnap.data()?.assetUploadFormId || null;

          if (ptr === formId) {
            await updateDoc(orgRef, {
              assetUploadFormId: null,
              assetUploadFormPublishedAt: null,
            });
            console.log(`[archiveFormAndRelatedData] Cleared organizations/${orgId}.assetUploadFormId for archived AUF ${formId}.`);
          }
        }
      }
    } catch (e) {
      console.warn('[archiveFormAndRelatedData] Could not clear org AUF pointer (non-fatal):', e);
    }

    return { status: 'success', message: `Form with ID ${formId} archived successfully.` };
  } catch (error) {
    console.error(`[archiveFormAndRelatedData] Error archiving form and related data for formId: ${formId}`, error);
    return { status: 'error', message: `Error archiving form with ID ${formId}: ${error.message}` };
  }
};


/**
 * Checks if the provided userId is the owner of the specified organization.
 * @param {string} organizationId - The ID of the organization to check.
 * @param {string} userId - The UID of the user to validate ownership.
 * @returns {Promise<boolean>} - Resolves to true if the user is the owner, false otherwise.
 */
const ownershipCache = new Map(); // Simple in-memory cache for ownership checks

export const isOrganizationOwner = async (organizationId, userId) => {
  // Check cache first to avoid unnecessary Firestore reads
  const cacheKey = `${organizationId}_${userId}`;
  if (ownershipCache.has(cacheKey)) {
    console.log(`[isOrganizationOwner] Cache hit for organizationId: ${organizationId} and userId: ${userId}`);
    return ownershipCache.get(cacheKey);
  }

  try {
    const orgRef = doc(db, 'organizations', organizationId);
    const orgSnap = await getDoc(orgRef);

    if (!orgSnap.exists()) {
      console.error(`[isOrganizationOwner] Organization with ID ${organizationId} does not exist.`);
      return false;
    }

    const orgData = orgSnap.data();

    // Ownership validation
    const isOwner = orgData.ownerId === userId;
    if (isOwner) {
      console.log(`[isOrganizationOwner] User ${userId} is confirmed as owner of organization ${organizationId}.`);
    } else {
      console.log(`[isOrganizationOwner] User ${userId} is NOT the owner of organization ${organizationId}.`);
    }

    // Cache the result for future calls
    ownershipCache.set(cacheKey, isOwner);
    return isOwner;
  } catch (error) {
    const errorMessage = `Failed to validate ownership for organization ${organizationId}: ${error.message}`;
    console.error(`[isOrganizationOwner] ${errorMessage}`);
    throw new Error(errorMessage);
  }
};

/**
 * Update a user's profile in Firestore with safeguards for critical fields like ownerId.
 * @param {string} uid - The UID of the user to update.
 * @param {Object} updatedData - The fields to update in the user's profile.
 * @returns {Promise<void>} Resolves when the update is successful.
 */
export const updateUserProfile = async (uid, updatedData) => {
  console.log(`[updateUserProfile] Attempting to update profile for UID: ${uid}`);

  const userRef = doc(db, 'accounts', uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    throw new Error(`[updateUserProfile] User with UID ${uid} does not exist.`);
  }

  const currentProfile = userSnap.data();

  // Safeguard to prevent modification of immutable fields
  if ('ownerId' in updatedData && updatedData.ownerId !== currentProfile.ownerId) {
    throw new Error(`[updateUserProfile] Cannot modify immutable field 'ownerId' for user ${uid}.`);
  }

  // Remove immutable fields from the update to avoid accidental overwrites
  const safeUpdates = { ...updatedData };
  delete safeUpdates.ownerId; // Ensure 'ownerId' is not modified

  try {
    await updateDoc(userRef, safeUpdates);
    console.log(`[updateUserProfile] Profile for UID ${uid} updated successfully:`, safeUpdates);
  } catch (error) {
    console.error(`[updateUserProfile] Error updating profile for UID ${uid}:`, error);
    throw error; // Rethrow for further handling upstream if necessary
  }
};


// Save Column Order to Firestore (User-Specific)
export const saveColumnOrderToFirestore = async (formId, userId, columnOrder) => {
  try {
    const columnSettingsRef = doc(db, "formColumnSettings", formId, "user_settings", userId);

    await setDoc(columnSettingsRef, { 
      formId,
      userId,
      columnOrder,
      lastUpdated: new Date().toISOString()
    }, { merge: true });

    console.log(`[Firestore] Column order saved successfully for user ${userId} on form ${formId}.`);
    return { status: "success" };
  } catch (error) {
    console.error("[Firestore] Error saving column order:", error);
    return { status: "error", message: error.message };
  }
};

// Load Column Order from Firestore (User-Specific)
export const getColumnOrderFromFirestore = async (formId, userId) => {
  try {
    const columnSettingsRef = doc(db, "formColumnSettings", formId, "user_settings", userId);
    const columnSettingsSnap = await getDoc(columnSettingsRef);

    if (columnSettingsSnap.exists()) {
      console.log(`[Firestore] Column order loaded for user ${userId} on form ${formId}:`, columnSettingsSnap.data().columnOrder);
      return columnSettingsSnap.data().columnOrder;
    } else {
      console.warn(`[Firestore] No column order found for user ${userId} on form ${formId}.`);
      return null; // Return null if no settings are saved
    }
  } catch (error) {
    console.error("[Firestore] Error loading column order:", error);
    return null;
  }
};

/**
 * Returns the total number of submissions for a form (no docs downloaded).
 * If startDate / endDate are provided they are applied as an inclusive range
 * filter against `submittedAt`.  Accepts plain JS `Date` objects.
 *
 * @param {string}                    formId               – ID of the form.
 * @param {Date|Timestamp|null}       startDate            – Lower-bound (inclusive) or null.
 * @param {Date|Timestamp|null}       endDate              – Upper-bound **date** (any time will be ignored) or null.
 * @param {string|null|undefined}     submitterId          – (OPTIONAL) When provided, count only rows where
 *                                                           submitterId === this value (e.g., per-viewer counts).
 * @param {string|null|undefined}     submitterSessionId   – (OPTIONAL) When provided, count only rows where
 *                                                           submitterSessionId === this value (per-browser-session).
 *                                                           If supplied, this takes precedence over submitterId.
 *
 * @return {Promise<number>}                               – Exact server-side count.
 */
export const getFormSubmissionsCount = async (
  formId,
  startDate = null,
  endDate   = null,
  submitterId = null,                    // ★ optional, backward-compatible
  submitterSessionId = null              // ★ NEW optional (preferred when present)
) => {
//  console.log("[utils/firestore] Counting submissions", {
//    formId,
//    startDate,
//    endDate,
//    submitterId,
//    submitterSessionId,                 // ★ NEW (safe to log; optional)
//  });

  /* ---------------------------------------------------------------
   * Build range clauses only if the caller supplied a window
   * ------------------------------------------------------------- */
  const rangeClauses = [];

  if (startDate) {
    const ts =
      startDate instanceof Timestamp
        ? startDate
        : Timestamp.fromDate(new Date(startDate));
    rangeClauses.push(where("submittedAt", ">=", ts));
  }

  if (endDate) {
    // user might pass YYYY-MM-DD 00:00 – bump to end-of-day
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    const ts =
      endDate instanceof Timestamp
        ? endDate
        : Timestamp.fromDate(end);
    rangeClauses.push(where("submittedAt", "<=", ts));
  }

  /* ---------------------------------------------------------------
   * Optional viewer clauses (keeps existing behavior when both null)
   *   • Prefer session-scoped filter when provided to isolate the
   *     current browser/incognito session.
   *   • Fallback to the long-lived submitterId if no session is given.
   * ------------------------------------------------------------- */
  const viewerClause = submitterSessionId
    ? [where("submitterSessionId", "==", submitterSessionId)]
    : (submitterId ? [where("submitterId", "==", submitterId)] : []);

  /* ---------------------------------------------------------------
   * Firestore aggregate count – **no limit** applied
   * ------------------------------------------------------------- */
  const q = query(
    collection(db, "formSubmissions"),
    where("formId", "==", formId),
    ...viewerClause,          // ★ NEW/EXISTING (optional, added safely)
    ...rangeClauses
  );

  const snap  = await getCountFromServer(q);     // ↞ v9.10+
  const total = snap.data().count;

//  console.log("[utils/firestore] Count result:", total);
  return total;                                  // integer
};



/**
 * Persist public / org-wide / per-user sharing settings.
 * – Pass only the keys you want to change; nothing is overwritten
 *   unless you supply the value.
 *
 *   await setFormSharingSettings('form123', {
 *     isPublic    : true,
 *     orgAccess   : 'view',          // 'none' | 'view' | 'comment' | 'edit'
 *     sharedWith  : { userA: 'edit', userB: 'view' }
 *   });
 */
export const setFormSharingSettings = async (formId, {
  isPublic,           // boolean      | undefined
  orgAccess,          // string       | undefined
  sharedWith,         // {uid: lvl..} | undefined
}) => {
  if (!formId) throw new Error('setFormSharingSettings: Missing formId');

  const formRef = doc(db, 'forms', formId);
  const payload = { lastModified: serverTimestamp() };

  if (isPublic   !== undefined) payload.isPublic       = !!isPublic;
  if (orgAccess  !== undefined) payload.orgAccess      = orgAccess;
  if (sharedWith !== undefined) {
    payload.sharedWith      = sharedWith;

    /* 🆕  scalable query field — array of UIDs */
    payload.sharedWithIds   = Object.keys(sharedWith);
  }

  await updateDoc(formRef, payload);
};


/**
 * Resolve the **highest** access a given user has for a form.
 * Returns: 'owner' | 'edit' | 'comment' | 'view' | 'none'
 */
export const getUserAccessForForm = async (formDoc, userId, orgId) => {
  if (!formDoc.exists()) return 'none';
  const data = formDoc.data();

  // 1) Owner?
  if (data.owner === userId) return 'owner';

  // 2) Direct share?
  if (data.sharedWith?.[userId]) return data.sharedWith[userId];

  // 3) Org-wide share?
  if (data.organizationId === orgId && data.orgAccess && data.orgAccess !== 'none')
    return data.orgAccess;

  // 4) Public?
  if (data.isPublic) return 'view';

  return 'none';
};


/**
 * Convenience check used all over the app:
 *   if (await userCan('edit', formId, currentUser.uid, orgId)) …
 */
export const userCan = async (capability, formId, userId, orgId) => {
  const formSnap = await getDoc(doc(db, 'forms', formId));

  // ──────────────────────────────────────────────────────────────
  // Global templates are viewable by any signed‑in user
  // (isTemplate === true  &&  templateScope === 'global')
  // ──────────────────────────────────────────────────────────────
  if (
    formSnap.exists() &&
    formSnap.data().isTemplate === true &&
    formSnap.data().templateScope === 'global'
  ) {
    return true;                 // ✔  skip the usual ACL check
  }

  const lvl = await getUserAccessForForm(formSnap, userId, orgId);

  //
  // index:     0        1      2        3
  const order = ['none', 'view', 'edit', 'owner'];
  const need  = capability === 'view'  ? 1   // ≥'view'⇒Viewer & Editor & Owner
                 : capability === 'edit'  ? 2   // ≥'edit'⇒Editor & Owner
                 : capability === 'owner' ? 3   // == 'owner'
                 : 0;

  return order.indexOf(lvl) >= need;
};


export async function getLayoutMeta(formId) {
  const snap = await getDoc(doc(db, "forms", formId));
  if (!snap.exists()) throw new Error("Form not found");
  return { lastModified: snap.data().lastModified?.toMillis() ?? 0 };
}


// ──────────────────────────────────────────────────────────────
// Vanity URL helpers
// ──────────────────────────────────────────────────────────────
//
// Enterprise rule:
// The client never writes vanityUrls/{slug} directly. Vanity URL
// mutations are owned by Cloud Functions so the form document and
// lookup registry stay consistent in one backend transaction.

const getVanityFunctions = () => getFunctions(app, 'us-central1');

export const checkVanitySlugAvailability = async (
  slug,
  formId = null
) => {
  const s = String(slug || '').trim().toLowerCase();
  const functions = getVanityFunctions();
  const callable = httpsCallable(
    functions,
    'checkVanitySlugAvailability'
  );

  const resp = await callable({
    slug: s,
    formId: formId || null,
  });

  return resp?.data || {
    ok: false,
    available: false,
    state: 'error',
  };
};

export const commitFormVanitySlug = async (
  formId,
  slugOrNull
) => {
  if (!formId) {
    throw new Error('commitFormVanitySlug: missing formId');
  }

  const slug =
    slugOrNull == null || String(slugOrNull).trim() === ''
      ? null
      : String(slugOrNull).trim().toLowerCase();

  const functions = getVanityFunctions();
  const callable = httpsCallable(functions, 'setFormVanitySlug');
  const resp = await callable({ formId, slug });

  if (!resp?.data?.ok) {
    throw new Error('Vanity URL update failed.');
  }

  return resp.data;
};

export const clearFormVanitySlug = async (formId) => (
  commitFormVanitySlug(formId, null)
);

/**
 * Deprecated compatibility wrapper.
 *
 * Do not use this for new code. Directly patching forms/{formId}
 * with vanitySlug bypasses the vanityUrls/{slug} registry and can
 * create broken routes.
 */
export const setFormVanitySlug = async (formId, slugOrNull) => {
  console.warn(
    '[setFormVanitySlug] Deprecated wrapper used. ' +
      'Use commitFormVanitySlug(formId, slugOrNull).'
  );

  return commitFormVanitySlug(formId, slugOrNull);
};

/**
 * Deprecated compatibility wrapper.
 *
 * Do not use this for new code. Clearing a vanity URL must be
 * form-scoped so the backend can update forms/{formId} and
 * vanityUrls/{slug} transactionally.
 */
export const deleteVanitySlug = async (_slug, formId = null) => {
  if (!formId) {
    const message =
      'deleteVanitySlug is deprecated. Use clearFormVanitySlug(formId).';

    console.warn('[deleteVanitySlug] Refused unscoped slug delete.', {
      message,
    });

    return {
      ok: false,
      mode: 'deprecated',
      error: message,
    };
  }

  return clearFormVanitySlug(formId);
};

/**
 * Deprecated compatibility wrapper.
 *
 * Direct registry reads should be replaced with
 * checkVanitySlugAvailability(slug, formId).
 */
export const getVanityMapping = async (slug, formId = null) => {
  const result = await checkVanitySlugAvailability(slug, formId);

  return {
    exists: result.state === 'owned' || result.state === 'taken',
    formId: result.formId || null,
    disabled: result.state === 'available',
    available: result.available === true,
    state: result.state || 'unknown',
  };
};

/**
 * Deprecated compatibility wrapper.
 *
 * Soft-disabling vanity mappings from the browser is no longer allowed.
 * Use clearFormVanitySlug(formId), which calls the backend transaction.
 */
export const disableVanitySlug = async (_slug) => {
  const message =
    'disableVanitySlug is deprecated. Use clearFormVanitySlug(formId).';

  console.warn('[disableVanitySlug] Refused client-side disable.', {
    message,
  });

  return {
    ok: false,
    mode: 'deprecated',
    error: message,
  };
};

